import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import * as api from '../../lib/api'
import { motion, AnimatePresence } from 'framer-motion'
import Modal from '../../components/Modal'
import DateRangeFilter from '../../components/DateRangeFilter'
import DatePickerField from '../../components/DatePickerField'
import CurrencyInput from '../../components/CurrencyInput'
import { useToast } from '../../contexts/ToastContext'
import { SkeletonGrid } from '../../components/Skeleton/Skeleton'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, PieChart, Pie, Cell, CartesianGrid } from 'recharts'
import ImportWizard from '../../components/ImportWizard/ImportWizard'
import CustomSelect from '../../components/CustomSelect/CustomSelect'
import DatePicker from '../../components/DatePicker/DatePicker'
import ExportAnalyticsModal from './components/ExportAnalyticsModal'
import './AnalyticsPage.css'

const todayIso = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const monthStartIso = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}
const monthEndIso = () => {
  const d = new Date()
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
}

const formatMoney = (val) => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(val || 0)

const clearLegacyAnalyticsCache = () => {
  try {
    Object.keys(localStorage).forEach(k => {
      if (k.startsWith('cache_analytics_')) {
        localStorage.removeItem(k)
      }
    })
  } catch {}
}

const getCachedState = (key, defaultVal) => {
  clearLegacyAnalyticsCache()
  return defaultVal
}

const setCachedState = (key, val) => {
  clearLegacyAnalyticsCache()
}

const EXPENSE_CATEGORIES = [
  { id: 'rent', label: 'Аренда', icon: '' },
  { id: 'utilities', label: 'Коммуналка', icon: '' },
  { id: 'internet', label: 'Интернет', icon: '' },
  { id: 'supplies', label: 'Хозтовары', icon: '' },
  { id: 'marketing', label: 'Реклама', icon: '' },
  { id: 'other', label: 'Прочие', icon: '' },
]

export default function AnalyticsPage() {
  const { profile, activePvz, adminPermissions: userPermissions } = useAuth()
  const toast = useToast()
  const [incomes, setIncomes] = useState([])
  const [expenses, setExpenses] = useState([])
  const [reserves, setReserves] = useState([])
  const [fixedPlans, setFixedPlans] = useState([])
  const [payments, setPayments] = useState([])
  const [deductions, setDeductions] = useState([])
  const [baseSalaryAccrued, setBaseSalaryAccrued] = useState(0)
  const [salaryList, setSalaryList] = useState([])
  const [settings, setSettings] = useState(null)
  
  const [loading, setLoading] = useState(true)

  // При смене активного ПВЗ или профиля пользователя немедленно обнуляем финансовые стейты
  useEffect(() => {
    setIncomes([])
    setExpenses([])
    setReserves([])
    setFixedPlans([])
    setPayments([])
    setDeductions([])
    setBaseSalaryAccrued(0)
    setSalaryList([])
    setSettings(null)
    setLoading(true)
  }, [activePvz?.id, profile?.id])
  
  const [filterStartDate, setFilterStartDate] = useState(monthStartIso())
  const [filterEndDate, setFilterEndDate] = useState(monthEndIso())
  const [useEstimatedRevenue, setUseEstimatedRevenue] = useState(false)
  const [issuedLogs, setIssuedLogs] = useState([])
  const [salaryConfig, setSalaryConfig] = useState(null)
  const [partners, setPartners] = useState([])
  const [partnerTurnovers, setPartnerTurnovers] = useState([])
  const [partnerModal, setPartnerModal] = useState(null) // null | 'add_partner' | 'add_turnover'
  const [editPartner, setEditPartner] = useState(null)

  // Модалки
  const [modalType, setModalType] = useState(null)
  const [dayDetailsData, setDayDetailsData] = useState(null)
  const [modalDate, setModalDate] = useState(todayIso())
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [showImportWizard, setShowImportWizard] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [modalSource, setModalSource] = useState('Выдача посылок')
  const [modalCategory, setModalCategory] = useState(EXPENSE_CATEGORIES[0]?.label || '')
  const [showExpensesDetails, setShowExpensesDetails] = useState(false)
  const [expenseDetailsSubScreen, setExpenseDetailsSubScreen] = useState(null)
  const [openEmployeeId, setOpenEmployeeId] = useState(null)

  // Вкладки для нижних списков
  const [activeTab, setActiveTab] = useState('incomes')
  const [registryModal, setRegistryModal] = useState(false)
  const [visibleFinMetrics, setVisibleFinMetrics] = useState({ income: true, expense: true, profit: true })
  const [isNoticeExpanded, setIsNoticeExpanded] = useState(false)
  
  // Состояния для удаления
  const [selectedItems, setSelectedItems] = useState([])
  const [itemToDelete, setItemToDelete] = useState(null)

  // Настройки моделирования прогноза в аналитике
  const [showForecastSettings, setShowForecastSettings] = useState(false)
  const [forecastAutoCalc, setForecastAutoCalc] = useState(false)
  const [forecastCalcPeriod, setForecastCalcPeriod] = useState('last_month') // 'last_month' | 'last_30_days'
  const [forecastManualRate, setForecastManualRate] = useState('15')
  const [isForecastSaving, setIsForecastSaving] = useState(false)
  const [autoCalcDetails, setAutoCalcDetails] = useState({ totalIncome: 0, issuedCount: 0, calculatedRate: 0, startDate: '', endDate: '' })
  const [averageDailyPayrollPast30, setAverageDailyPayrollPast30] = useState(0)
  const [averageDailyVarExpensesPast30, setAverageDailyVarExpensesPast30] = useState(0)
  const [averageDailyPartnerCommissionPast30, setAverageDailyPartnerCommissionPast30] = useState(0)
  const [employeePastAvgPayroll, setEmployeePastAvgPayroll] = useState({})
  
  const isOwner = profile?.role === 'owner'
  const canSeeAnalytics = isOwner || (userPermissions?.canSeeIncome === true)

  useEffect(() => {
    if (activePvz?.id && canSeeAnalytics) loadData()
  }, [activePvz?.id, filterStartDate, filterEndDate, canSeeAnalytics])

  async function loadData(silent = false) {
    if (!silent) setLoading(true)
    try {
      const pvzId = activePvz.id
      const pStart = filterStartDate
      const pEnd = filterEndDate

      const startParts = pStart.split('-').map(Number);
      const startDateObj = new Date(startParts[0], startParts[1] - 1, startParts[2]);
      startDateObj.setDate(startDateObj.getDate() - 30);
      const pStartMinus30 = `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-${String(startDateObj.getDate()).padStart(2, '0')}`;

      const [inc, exp, pay, ded, emps, rsv, plans, sets, issued, salCfg] = await Promise.all([
        api.getWbIncomes(pvzId, pStartMinus30, pEnd),
        api.getExpenses(pvzId, pStartMinus30, pEnd), // Расширили назад на 30 дней для расчета среднесуточных расходов
        api.getPayments({ periodStart: pStart, periodEnd: pEnd, pvzId }),
        api.getDeductions(pStart, pEnd, pvzId),
        api.getEmployeesList(pvzId),
        api.getReserves(pvzId, pStart, pEnd),
        api.getFixedExpenseTemplates(pvzId),
        api.getAnalyticsSettings(pvzId),
        api.getWorkLogs('issued', { startDate: pStartMinus30, endDate: pEnd, pvzId }),
        api.getSalaryConfig()
      ])
      
      const [rawSalaries, rawPastSalaries] = await Promise.all([
        api.calculateTeamSalaries(pvzId, pStart, pEnd, emps.filter(e => !e.is_legacy), [], { mode: 'analytics' }),
        api.calculateTeamSalaries(pvzId, pStartMinus30, pStart, emps.filter(e => !e.is_legacy), [], { mode: 'analytics' }) // Запрос ФОТ за прошлые 30 дней
      ])
      
      const salaries = rawSalaries.map(sal => ({
        ...sal,
        shiftsCount: sal.daysWorked
      }))

      // Считаем корректный ФОТ: сумма итоговых зарплат к выплате, игнорируя минусовые балансы
      const calcPayroll = salaries.reduce((sum, s) => sum + Math.max(s.total || 0, 0), 0)

      // Расчет среднесуточных показателей за прошлые 30 дней
      const pastPayrollTotal = rawPastSalaries.reduce((sum, s) => sum + Math.max(s.total || 0, 0), 0)
      const avgDailyPayroll = pastPayrollTotal / 30
      setAverageDailyPayrollPast30(avgDailyPayroll)

      const empForecastMap = {}
      rawPastSalaries.forEach(sal => {
        empForecastMap[sal.employeeId] = Math.max(sal.total || 0, 0) / 30
      })
      setEmployeePastAvgPayroll(empForecastMap)

      const pastExpenses = (exp || []).filter(e => e.date >= pStartMinus30 && e.date < pStart && e.type !== 'fixed_payment' && e.is_paid !== false)
      const pastVarExpensesTotal = pastExpenses.reduce((sum, e) => sum + Number(e.amount), 0)
      const avgDailyVarExpenses = pastVarExpensesTotal / 30
      setAverageDailyVarExpensesPast30(avgDailyVarExpenses)

      setIncomes(inc || [])
      setExpenses(exp || [])
      setPayments(pay || [])
      setDeductions(ded || [])
      setReserves(rsv || [])
      setFixedPlans(plans || [])
      setBaseSalaryAccrued(calcPayroll)
      setSalaryList(salaries)
      setSettings(sets || {})
      setIssuedLogs(issued || [])
      setSalaryConfig(salCfg || {})

      // Инициализация настроек авторасчета прогноза
      const isAuto = salCfg?.wbRevenueAutoCalc !== undefined ? salCfg.wbRevenueAutoCalc : true
      const period = salCfg?.wbRevenueCalcPeriod || 'last_month'
      const manualVal = salCfg?.wbRevenuePerIssued || 3.0
      const manualRate = manualVal > 10 ? 3.0 : manualVal; // Сброс старых значений в рублях (например, 15) в разумный процент

      setForecastAutoCalc(isAuto)
      setForecastCalcPeriod(period)
      setForecastManualRate(manualRate)

      // Загрузка партнёров
      try {
        const currentPeriod = pStart.substring(0, 7) // 'YYYY-MM'
        const [pts, ptTurnovers] = await Promise.all([
          api.getPartners(pvzId),
          api.getPartnerTurnovers(pvzId, pStartMinus30, pEnd) // Расширили назад на 30 дней для расчета среднесуточных расходов
        ])
        setPartners(pts || [])
        setPartnerTurnovers(ptTurnovers || [])

        // Расчет среднесуточной партнерской комиссии за прошлые 30 дней
        const pastPartnerTurnovers = (ptTurnovers || []).filter(t => {
          const d = t.entry_date?.split('T')[0]
          return d >= pStartMinus30 && d < pStart
        })
        const pastPartnerTotal = pastPartnerTurnovers.reduce((sum, t) => sum + Number(t.commission_amount || 0), 0)
        const avgDailyPartnerCommission = pastPartnerTotal / 30
        setAverageDailyPartnerCommissionPast30(avgDailyPartnerCommission)
      } catch (e) { console.error('[Partners] Load error:', e) }

      // Save to cache
      if (pStart === monthStartIso() && filterEndDate === monthEndIso()) {
        setCachedState('incomes', inc || [])
        setCachedState('expenses', exp || [])
        setCachedState('payments', pay || [])
        setCachedState('deductions', ded || [])
        setCachedState('reserves', rsv || [])
        setCachedState('fixedPlans', plans || [])
        setCachedState('baseSalaryAccrued', calcPayroll)
        setCachedState('settings', sets || {})
      }
    } catch (err) {
      console.error(err)
      toast.error('Ошибка загрузки аналитики')
    } finally {
      setLoading(false)
    }
  }

  const handleForecastSettingUpdate = async (newAutoCalc, newPeriod, newManualRateVal) => {
    setIsForecastSaving(true)
    try {
      const rateToSave = Number(newManualRateVal) || 3.0

      const newConfig = {
        ...salaryConfig,
        wbRevenueAutoCalc: newAutoCalc,
        wbRevenueCalcPeriod: newPeriod,
        wbRevenuePerIssued: rateToSave
      }

      await api.updateSalaryConfig(newConfig, profile?.id)
      setSalaryConfig(newConfig)
      
      setForecastAutoCalc(newAutoCalc)
      setForecastCalcPeriod(newPeriod)
      setForecastManualRate(rateToSave)

      toast.success('Настройки прогноза сохранены!')
      loadData(true)
    } catch (err) {
      console.error(err)
      toast.error('Не удалось сохранить настройки')
    } finally {
      setIsForecastSaving(false)
    }
  }

  const [editItem, setEditItem] = useState(null)

  const getDefaultDate = () => {
    const today = todayIso()
    if (today >= filterStartDate && today <= filterEndDate) return today
    return filterEndDate
  }

  const openModal = (type, planId = '', item = null) => {
    setModalType(type)
    setModalDate(item?.date || item?.period_start || getDefaultDate())
    setSelectedPlanId(planId)
    setEditItem(item)
  }

  const handleDelete = (type, id) => {
    setItemToDelete({ type, id });
    setModalType('confirm_delete');
  }

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      const { type, id } = itemToDelete;
      if (type === 'bulk') {
        const ids = id; // массив ID
        if (activeTab === 'incomes') {
          await Promise.all(ids.map(i => api.deleteWbIncome(i)));
        } else if (activeTab === 'expenses') {
          await Promise.all(ids.map(i => api.deleteExpense(i)));
        } else if (activeTab === 'reserves') {
          await Promise.all(ids.map(i => api.deleteReserve(i)));
        }
      } else {
        if (type === 'income') await api.deleteWbIncome(id);
        if (type === 'expense') await api.deleteExpense(id);
        if (type === 'reserve') await api.deleteReserve(id);
        if (type === 'plan') {
          try {
            await api.deleteFixedExpenseTemplate(id);
          } catch(e) {
            await api.updateFixedExpenseTemplate(id, { is_active: false });
          }
        }
      }
      toast.success('Успешно удалено');
      setSelectedItems([]);
      setModalType(null);
      setItemToDelete(null);
      loadData(true);
    } catch (err) {
      toast.error('Ошибка при удалении: ' + err.message);
    }
  }

  const handleExpenseGroupClick = (subScreen) => {
    setExpenseDetailsSubScreen(subScreen);
  }

  const toggleSelection = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }

  const toggleSelectAll = (items) => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(i => i.id));
    }
  }

  if (!canSeeAnalytics) {
    return <div className="analytics-page"><div className="empty-state">У вас нет доступа к этому разделу.</div></div>
  }

  // --- РАСЧЕТЫ (ФАКТ И УМНЫЙ ПРОГНОЗ) ---
  const displayedIncomes = useMemo(() => {
    return incomes.filter(i => i.date >= filterStartDate && i.date <= filterEndDate)
  }, [incomes, filterStartDate, filterEndDate])

  const dailyFinancials = useMemo(() => {
    const days = [];
    const startParts = filterStartDate.split('-').map(Number);
    const endParts = filterEndDate.split('-').map(Number);
    let currentDay = new Date(startParts[0], startParts[1] - 1, startParts[2]);
    const endDay = new Date(endParts[0], endParts[1] - 1, endParts[2]);
    
    while (currentDay <= endDay) {
      const yyyy = currentDay.getFullYear();
      const mm = String(currentDay.getMonth() + 1).padStart(2, '0');
      const dd = String(currentDay.getDate()).padStart(2, '0');
      days.push(`${yyyy}-${mm}-${dd}`);
      currentDay.setDate(currentDay.getDate() + 1);
    }

    // 1. Построим ежедневную карту фактических доходов WB с распределением по дням отчета
    const dailyFactMap = {};
    const actualWbIncomes = incomes.filter(i => (i.source === 'Wildberries' || !i.source));
    
    actualWbIncomes.forEach(i => {
      const startStr = i.period_start || i.date;
      const endStr = i.period_end || i.date;
      if (!startStr || !endStr) return;
      
      const startParts = startStr.split('-').map(Number);
      const endParts = endStr.split('-').map(Number);
      const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2]);
      const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);
      
      const daysInPeriod = [];
      let cur = new Date(startDate);
      while (cur <= endDate) {
        const yyyy = cur.getFullYear();
        const mm = String(cur.getMonth() + 1).padStart(2, '0');
        const dd = String(cur.getDate()).padStart(2, '0');
        daysInPeriod.push(`${yyyy}-${mm}-${dd}`);
        cur.setDate(cur.getDate() + 1);
      }
      
      const N = daysInPeriod.length;
      if (N === 0) return;
      
      const amountTotal = Number(i.amount||0) + Number(i.compensation||0) + Number(i.bonus||0) - Number(i.penalty||0);
      const turnoverTotal = Number(i.turnover || 0) > 0 ? Number(i.turnover) : amountTotal / 0.03;
      const saleByTariffTotal = Number(i.sale_by_tariff || 0) > 0 ? Number(i.sale_by_tariff) : turnoverTotal * 0.03;

      // Найдем логи выдачи за эти дни
      const periodIssuedLogs = issuedLogs.filter(l => l.work_date >= startStr && l.work_date <= endStr);
      const totalIssuedCount = periodIssuedLogs.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
      
      daysInPeriod.forEach(d => {
        let ratio = 1 / N;
        if (totalIssuedCount > 0) {
          const dayLogs = periodIssuedLogs.filter(l => l.work_date === d);
          const dayCount = dayLogs.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
          ratio = dayCount / totalIssuedCount;
        }
        
        if (!dailyFactMap[d]) {
          dailyFactMap[d] = { income: 0, turnover: 0, sale_by_tariff: 0 };
        }
        dailyFactMap[d].income += amountTotal * ratio;
        dailyFactMap[d].turnover += turnoverTotal * ratio;
        dailyFactMap[d].sale_by_tariff += saleByTariffTotal * ratio;
      });
    });

    let lastActualDate = null;
    if (actualWbIncomes.length > 0) {
      lastActualDate = actualWbIncomes.reduce((max, i) => {
        const val = i.period_end || i.date;
        return val > max ? val : max;
      }, actualWbIncomes[0].period_end || actualWbIncomes[0].date);
    }

    let averageDailyTurnover = 0;
    if (lastActualDate) {
      const lastDateObj = new Date(lastActualDate + 'T00:00:00');
      let count = 0;
      let sum = 0;
      for (let offset = 0; offset < 30; offset++) {
        const dObj = new Date(lastDateObj.getTime());
        dObj.setDate(dObj.getDate() - offset);
        const yyyy = dObj.getFullYear();
        const mm = String(dObj.getMonth() + 1).padStart(2, '0');
        const dd = String(dObj.getDate()).padStart(2, '0');
        const dateStr = `${yyyy}-${mm}-${dd}`;
        
        if (dailyFactMap[dateStr]) {
          sum += dailyFactMap[dateStr].turnover;
          count++;
        }
      }
      if (count > 0) {
        averageDailyTurnover = sum / count;
      }
    }

    if (averageDailyTurnover === 0) {
      const totalIssued = issuedLogs.reduce((acc, l) => acc + (l.count || 0), 0);
      const totalDays = issuedLogs.length || 1;
      averageDailyTurnover = (totalIssued / totalDays) * 2500;
    }
    if (averageDailyTurnover === 0) {
      averageDailyTurnover = 150000;
    }

    const getTurnoverForDate = (dateStr) => {
      if (dailyFactMap[dateStr]) {
        return dailyFactMap[dateStr].turnover;
      }
      if (lastActualDate && dateStr <= lastActualDate) {
        return 0;
      }
      const logMatches = issuedLogs.filter(l => l.work_date === dateStr);
      const dayCount = logMatches.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
      if (dayCount > 0) {
        return dayCount * 2500;
      }
      return averageDailyTurnover;
    };

    const dailyData = {};
    days.forEach(d => {
      let income = 0;
      let turnover = 0;
      let tariffPercent = 0;
      let type = 'fact';

      if (lastActualDate && d <= lastActualDate) {
        type = 'fact';
        if (dailyFactMap[d]) {
          income = dailyFactMap[d].income;
          turnover = dailyFactMap[d].turnover;
          tariffPercent = turnover > 0 ? (dailyFactMap[d].sale_by_tariff / turnover) * 100 : 3.0;
        } else {
          income = 0;
          turnover = 0;
          tariffPercent = 3.0;
        }
      } else {
        type = 'forecast';
        if (forecastAutoCalc) {
          let rollingTurnover = 0;
          const dObj = new Date(d + 'T00:00:00');
          for (let i = 1; i <= 30; i++) {
            const tObj = new Date(dObj.getTime());
            tObj.setDate(tObj.getDate() - i);
            const yyyy = tObj.getFullYear();
            const mm = String(tObj.getMonth() + 1).padStart(2, '0');
            const dd = String(tObj.getDate()).padStart(2, '0');
            const tStr = `${yyyy}-${mm}-${dd}`;
            rollingTurnover += getTurnoverForDate(tStr);
          }
          tariffPercent = 3.885 - (rollingTurnover / 9000000);
          tariffPercent = Math.max(1.5, Math.min(4.5, tariffPercent));
        } else {
          tariffPercent = Number(forecastManualRate) || 3.0;
        }

        const logMatches = issuedLogs.filter(l => l.work_date === d);
        const dayCount = logMatches.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
        if (dayCount > 0) {
          turnover = dayCount * 2500;
        } else {
          turnover = averageDailyTurnover;
        }
        income = turnover * (tariffPercent / 100);
      }

      dailyData[d] = {
        date: d,
        income,
        turnover,
        tariffPercent,
        type
      };
    });

    return { dailyData, days, lastActualDate, averageDailyTurnover, getTurnoverForDate };
  }, [incomes, issuedLogs, filterStartDate, filterEndDate, forecastAutoCalc, forecastManualRate]);

  const actualWbIncome = useMemo(() => {
    return Object.values(dailyFinancials.dailyData)
      .filter(day => day.type === 'fact')
      .reduce((sum, day) => sum + day.income, 0);
  }, [dailyFinancials]);
  
  const estimatedWbIncome = useMemo(() => {
    return Object.values(dailyFinancials.dailyData).reduce((sum, day) => sum + day.income, 0)
  }, [dailyFinancials])
  
  const totalWbIncome = useEstimatedRevenue ? estimatedWbIncome : actualWbIncome

  const otherIncomeList = useMemo(() => {
    return incomes.filter(i => i.date >= filterStartDate && i.date <= filterEndDate && i.source && i.source !== 'Wildberries')
  }, [incomes, filterStartDate, filterEndDate])
  
  const totalOtherIncome = useMemo(() => {
    return otherIncomeList.reduce((sum, i) => sum + Number(i.amount||0), 0)
  }, [otherIncomeList])
  
  const totalIncome = totalWbIncome + totalOtherIncome

  const currentRollingTurnover = useMemo(() => {
    if (!dailyFinancials.lastActualDate) return 0;
    const dObj = new Date(dailyFinancials.lastActualDate + 'T00:00:00');
    let rolling = 0;
    
    const localFactMap = {};
    const actualIncomes = incomes.filter(i => i.source === 'Wildberries' || !i.source);
    
    actualIncomes.forEach(i => {
      const startStr = i.period_start || i.date;
      const endStr = i.period_end || i.date;
      if (!startStr || !endStr) return;
      
      const startParts = startStr.split('-').map(Number);
      const endParts = endStr.split('-').map(Number);
      const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2]);
      const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);
      
      const daysInPeriod = [];
      let cur = new Date(startDate);
      while (cur <= endDate) {
        const yyyy = cur.getFullYear();
        const mm = String(cur.getMonth() + 1).padStart(2, '0');
        const dd = String(cur.getDate()).padStart(2, '0');
        daysInPeriod.push(`${yyyy}-${mm}-${dd}`);
        cur.setDate(cur.getDate() + 1);
      }
      
      const N = daysInPeriod.length;
      if (N === 0) return;
      
      const amountTotal = Number(i.amount||0) + Number(i.compensation||0) + Number(i.bonus||0) - Number(i.penalty||0);
      const turnoverTotal = Number(i.turnover || 0) > 0 ? Number(i.turnover) : amountTotal / 0.03;
      
      const periodIssuedLogs = issuedLogs.filter(l => l.work_date >= startStr && l.work_date <= endStr);
      const totalIssuedCount = periodIssuedLogs.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
      
      daysInPeriod.forEach(d => {
        let ratio = 1 / N;
        if (totalIssuedCount > 0) {
          const dayLogs = periodIssuedLogs.filter(l => l.work_date === d);
          const dayCount = dayLogs.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
          ratio = dayCount / totalIssuedCount;
        }
        localFactMap[d] = (localFactMap[d] || 0) + (turnoverTotal * ratio);
      });
    });

    for (let i = 0; i < 30; i++) {
      const tObj = new Date(dObj.getTime());
      tObj.setDate(tObj.getDate() - i);
      const yyyy = tObj.getFullYear();
      const mm = String(tObj.getMonth() + 1).padStart(2, '0');
      const dd = String(tObj.getDate()).padStart(2, '0');
      const tStr = `${yyyy}-${mm}-${dd}`;
      
      if (localFactMap[tStr]) {
        rolling += localFactMap[tStr];
      } else {
        const logMatches = issuedLogs.filter(l => l.work_date === tStr);
        const dayCount = logMatches.reduce((sum, l) => sum + (Number(l.count) || 0), 0);
        if (dayCount > 0) {
          rolling += dayCount * 2500;
        }
      }
    }
    return rolling;
  }, [incomes, issuedLogs, dailyFinancials.lastActualDate])

  const currentForecastTariff = useMemo(() => {
    if (currentRollingTurnover === 0) return 3.25;
    if (!forecastAutoCalc) return Number(forecastManualRate) || 3.0;
    const rate = 3.885 - (currentRollingTurnover / 9000000);
    return Math.max(1.5, Math.min(4.5, rate));
  }, [currentRollingTurnover, forecastAutoCalc, forecastManualRate])

  const maxIncomeDateStr = useMemo(() => {
    if (!displayedIncomes || displayedIncomes.length === 0) return null;
    let maxTime = -Infinity;
    let maxStr = null;
    displayedIncomes.forEach(i => {
      const dateStr = i.period_end || i.date;
      if (dateStr) {
        const time = new Date(dateStr + 'T23:59:59').getTime();
        if (time > maxTime) {
          maxTime = time;
          maxStr = dateStr;
        }
      }
    });
    return maxStr;
  }, [displayedIncomes]);

  // Отфильтрованные по выбранному периоду расходы и партнерские обороты (поскольку из БД мы их тянем за 60 дней)
  const selectedPeriodExpenses = useMemo(() => expenses.filter(e => e.date >= filterStartDate && e.date <= filterEndDate), [expenses, filterStartDate, filterEndDate])
  const selectedPeriodPartnerTurnovers = useMemo(() => partnerTurnovers.filter(t => {
    const d = t.entry_date?.split('T')[0]
    return d >= filterStartDate && d <= filterEndDate
  }), [partnerTurnovers, filterStartDate, filterEndDate])

  // Считаем количество прогнозных (будущих) дней в выбранном периоде
  const forecastDaysCount = useMemo(() => {
    if (!useEstimatedRevenue || !dailyFinancials.lastActualDate) return 0;
    return dailyFinancials.days.filter(d => d > dailyFinancials.lastActualDate).length;
  }, [useEstimatedRevenue, dailyFinancials])

  const pStartMinus30 = useMemo(() => {
    const startParts = filterStartDate.split('-').map(Number);
    const startDateObj = new Date(startParts[0], startParts[1] - 1, startParts[2]);
    startDateObj.setDate(startDateObj.getDate() - 30);
    return `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-${String(startDateObj.getDate()).padStart(2, '0')}`;
  }, [filterStartDate])

  const partnerForecastDetails = useMemo(() => {
    const forecasts = {}
    if (!useEstimatedRevenue || forecastDaysCount <= 0) return forecasts;
    
    partners.forEach(partner => {
      const pastPartnerTurnoversForP = partnerTurnovers.filter(t => {
        const d = t.entry_date?.split('T')[0]
        return t.partner_id === partner.id && d >= pStartMinus30 && d < filterStartDate
      })
      const pastTurnoverTotalForP = pastPartnerTurnoversForP.reduce((sum, t) => sum + Number(t.turnover_amount || 0), 0)
      const pastCommissionTotalForP = pastPartnerTurnoversForP.reduce((sum, t) => sum + Number(t.commission_amount || 0), 0)
      
      forecasts[partner.id] = {
        turnover: (pastTurnoverTotalForP / 30) * forecastDaysCount,
        commission: (pastCommissionTotalForP / 30) * forecastDaysCount,
        pvzRevenue: ((pastTurnoverTotalForP / 30) * forecastDaysCount) * (currentForecastTariff / 100)
      }
    })
    return forecasts
  }, [partners, partnerTurnovers, useEstimatedRevenue, forecastDaysCount, pStartMinus30, filterStartDate, currentForecastTariff])

  const partnerDailyData = useMemo(() => {
    const result = {}; // partnerId -> { [date]: { turnover, commission } }
    partners.forEach(partner => {
      result[partner.id] = {};
      
      // Фактические записи
      const entries = partnerTurnovers.filter(t => t.partner_id === partner.id);
      entries.forEach(e => {
        const d = e.entry_date?.split('T')[0];
        if (!result[partner.id][d]) {
          result[partner.id][d] = { turnover: 0, commission: 0 };
        }
        result[partner.id][d].turnover += Number(e.turnover_amount || 0);
        result[partner.id][d].commission += Number(e.commission_amount || 0);
      });

      // Прогнозные записи
      const fDetails = partnerForecastDetails[partner.id];
      if (useEstimatedRevenue && forecastDaysCount > 0 && fDetails) {
        const dailyForecastTurnover = fDetails.turnover / forecastDaysCount;
        const dailyForecastCommission = fDetails.commission / forecastDaysCount;

        dailyFinancials.days.forEach(d => {
          if (d > dailyFinancials.lastActualDate) {
            if (!result[partner.id][d]) {
              result[partner.id][d] = { turnover: 0, commission: 0 };
            }
            result[partner.id][d].turnover += dailyForecastTurnover;
            result[partner.id][d].commission += dailyForecastCommission;
          }
        });
      }
    });
    return result;
  }, [partners, partnerTurnovers, partnerForecastDetails, useEstimatedRevenue, forecastDaysCount, dailyFinancials]);

  const partnerMetricsAnalysis = useMemo(() => {
    const analysis = {}; // partnerId -> { averageTariff, averageTariffWithout, tariffLoss, taxAmount, netBenefit, ownTurnover }

    partners.forEach(partner => {
      let sumOwnTurnover = 0;
      let sumOwnTurnoverTariff = 0;
      let sumOwnTurnoverTariffWithout = 0;
      let totalTariffLoss = 0;

      dailyFinancials.days.forEach(d => {
        if (!useEstimatedRevenue && dailyFinancials.lastActualDate && d > dailyFinancials.lastActualDate) {
          return;
        }
        const totalTurnover = dailyFinancials.dailyData[d]?.turnover || 0;
        const partnerTurnover = partnerDailyData[partner.id]?.[d]?.turnover || 0;
        const ownTurnover = Math.max(0, totalTurnover - partnerTurnover);
        const tariffCurrent = dailyFinancials.dailyData[d]?.tariffPercent || 3.0;

        // Посчитаем rollingOwn30
        let rollingOwn30 = 0;
        const dObj = new Date(d + 'T00:00:00');
        for (let i = 1; i <= 30; i++) {
          const tObj = new Date(dObj.getTime());
          tObj.setDate(tObj.getDate() - i);
          const yyyy = tObj.getFullYear();
          const mm = String(tObj.getMonth() + 1).padStart(2, '0');
          const dd = String(tObj.getDate()).padStart(2, '0');
          const tStr = `${yyyy}-${mm}-${dd}`;

          const dayTotalTurnover = dailyFinancials.dailyData[tStr]
            ? dailyFinancials.dailyData[tStr].turnover
            : dailyFinancials.getTurnoverForDate(tStr);

          const dayPartnerTurnover = partnerDailyData[partner.id]?.[tStr]?.turnover || 0;
          rollingOwn30 += Math.max(0, dayTotalTurnover - dayPartnerTurnover);
        }

        // Альтернативный тариф
        let tariffWithout = 3.885 - (rollingOwn30 / 9000000);
        tariffWithout = Math.max(1.5, Math.min(4.5, tariffWithout));

        sumOwnTurnover += ownTurnover;
        sumOwnTurnoverTariff += ownTurnover * tariffCurrent;
        sumOwnTurnoverTariffWithout += ownTurnover * tariffWithout;

        const dayLoss = ownTurnover * (tariffCurrent - tariffWithout) / 100;
        totalTariffLoss += dayLoss;
      });

      // Рассчитаем итоговые значения по партнеру (все выплаты и поступления за выбранный период)
      const entries = partnerTurnovers.filter(t => {
        const d = t.entry_date?.split('T')[0];
        return t.partner_id === partner.id && d >= filterStartDate && d <= filterEndDate;
      });

      const actualTurnover = entries.reduce((s, e) => s + Number(e.turnover_amount), 0);
      const actualCommission = entries.reduce((s, e) => s + Number(e.commission_amount), 0);

      const actualPvzRevenue = entries.reduce((sum, e) => {
        const dateStr = e.entry_date?.split('T')[0];
        const dayData = dailyFinancials.dailyData[dateStr];
        const dayTariff = dayData ? dayData.tariffPercent : currentForecastTariff;
        return sum + (Number(e.turnover_amount) * (dayTariff / 100));
      }, 0);

      const fDetails = partnerForecastDetails[partner.id] || { turnover: 0, commission: 0, pvzRevenue: 0 };
      const totalPvzRevenue = actualPvzRevenue + fDetails.pvzRevenue;
      const totalCommission = actualCommission + fDetails.commission;
      
      const taxAmount = totalPvzRevenue * 0.06;
      const netBenefit = totalPvzRevenue - totalCommission - taxAmount + totalTariffLoss;

      analysis[partner.id] = {
        ownTurnover: sumOwnTurnover,
        averageTariff: sumOwnTurnover > 0 ? (sumOwnTurnoverTariff / sumOwnTurnover) : currentForecastTariff,
        averageTariffWithout: sumOwnTurnover > 0 ? (sumOwnTurnoverTariffWithout / sumOwnTurnover) : currentForecastTariff,
        tariffLoss: totalTariffLoss,
        taxAmount,
        netBenefit
      };
    });

    return analysis;
  }, [partners, partnerDailyData, partnerForecastDetails, partnerTurnovers, dailyFinancials, filterStartDate, filterEndDate, currentForecastTariff, useEstimatedRevenue, forecastDaysCount]);

  // ФОТ берется напрямую из подсчета зарплат каждого сотрудника (calcPayroll) + прогнозный ФОТ для будущих дней
  const payrollTotal = (baseSalaryAccrued || 0) + (useEstimatedRevenue ? (forecastDaysCount * averageDailyPayrollPast30) : 0)

  const varExpensesList = selectedPeriodExpenses.filter(e => e.type !== 'fixed_payment' && e.is_paid !== false)
  const actualVariableExpenses = varExpensesList.reduce((sum, e) => sum + Number(e.amount), 0)
  const variableExpenses = actualVariableExpenses + (useEstimatedRevenue ? (forecastDaysCount * averageDailyVarExpensesPast30) : 0)

  const activePlans = fixedPlans.filter(p => p.is_active !== false)
  const fixedPlanTotal = activePlans.reduce((sum, p) => sum + Number(p.amount || p.monthly_amount || 0), 0)

  // Smart dynamic proration restricted to latest income date or end of selected period in forecast
  const fixedPaidExpenses = activePlans.reduce((sum, p) => {
    const amount = Number(p.amount || p.monthly_amount || 0);
    if (amount <= 0 || isNaN(amount)) return sum;
    
    if (!maxIncomeDateStr) return sum; // No incomes = 0 constant expenses!
    
    const rStart = new Date(filterStartDate + 'T00:00:00');
    const rEnd = new Date(filterEndDate + 'T23:59:59');
    
    const cutoffDateStr = useEstimatedRevenue ? filterEndDate : (maxIncomeDateStr || filterStartDate);
    const cutoffEnd = new Date(Math.min(rEnd.getTime(), new Date(cutoffDateStr + 'T23:59:59').getTime()));
    
    if (rStart > cutoffEnd) return sum;
    
    let proratedTotal = 0;
    let current = new Date(rStart.getFullYear(), rStart.getMonth(), 1);
    
    while (current <= cutoffEnd) {
      const mYear = current.getFullYear();
      const mMonth = current.getMonth();
      const daysInMonth = new Date(mYear, mMonth + 1, 0).getDate();
      
      const mStart = new Date(mYear, mMonth, 1);
      const mEnd = new Date(mYear, mMonth + 1, 0, 23, 59, 59);
      
      const overlapStart = Math.max(mStart.getTime(), rStart.getTime());
      const overlapEnd = Math.min(mEnd.getTime(), cutoffEnd.getTime());
      
      if (overlapStart <= overlapEnd) {
        const overlapDays = Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24));
        proratedTotal += (amount / daysInMonth) * overlapDays;
      }
      
      current.setMonth(current.getMonth() + 1);
    }
    
    return sum + proratedTotal;
  }, 0)
  
  const actualReserves = reserves.reduce((sum, r) => sum + Number(r.amount), 0)

  const taxAmount = totalIncome * 0.06

  const actualPartnerCommission = selectedPeriodPartnerTurnovers.reduce((sum, t) => sum + Number(t.commission_amount || 0), 0)
  const totalPartnerCommission = actualPartnerCommission + (useEstimatedRevenue ? (forecastDaysCount * averageDailyPartnerCommissionPast30) : 0)

  const totalExpensesAll = payrollTotal + variableExpenses + fixedPaidExpenses + taxAmount + totalPartnerCommission

  const actualFreeCashFlow = totalIncome - payrollTotal - variableExpenses - fixedPaidExpenses - taxAmount - totalPartnerCommission

  const pctPayroll = totalExpensesAll > 0 ? (payrollTotal / totalExpensesAll) * 100 : 0
  const pctFixed = totalExpensesAll > 0 ? (fixedPaidExpenses / totalExpensesAll) * 100 : 0
  const pctVar = totalExpensesAll > 0 ? (variableExpenses / totalExpensesAll) * 100 : 0
  const pctTax = totalExpensesAll > 0 ? (taxAmount / totalExpensesAll) * 100 : 0
  const pctPartner = totalExpensesAll > 0 ? (totalPartnerCommission / totalExpensesAll) * 100 : 0

  // --- РАСЧЕТЫ (ПЛАН И РЕКОМЕНДАЦИИ) ---
  const remainingToPay = activePlans.reduce((sum, p) => {
    const paid = expenses.filter(e => e.fixed_plan_id === p.id && e.is_paid !== false).reduce((s, e) => s + Number(e.amount), 0);
    const planAmount = Number(p.amount || p.monthly_amount || 0);
    return sum + Math.max(planAmount - paid, 0);
  }, 0);
  const remainingToReserve = Math.max(fixedPlanTotal - actualReserves, 0)
  const expectedWbPayments = settings?.expected_wb_payments || 0
  const recommendedReservePerWbPayment = expectedWbPayments > 0 ? Math.round(fixedPlanTotal / expectedWbPayments) : 0

  const hasAnyData = incomes.length > 0 || expenses.length > 0 || fixedPlans.length > 0 || reserves.length > 0 || partnerTurnovers.length > 0;
  const showCharts = chartData => chartData.flowChart.length > 0 && chartData.expenseStruct.length > 0;

  // Данные для графиков — полностью самодостаточное вычисление
  const chartData = useMemo(() => {
    // Вычисляем все промежуточные переменные ВНУТРИ useMemo, чтобы избежать гонки рендеров
    const localActivePlans = fixedPlans.filter(p => p.is_active !== false)
    
    const dailyMap = {}
    
    // Хелпер для форматирования даты в YYYY-MM-DD без сдвига часовых поясов
    const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    
    // Собираем все даты в выбранном периоде
    const allDates = [];
    const startParts = filterStartDate.split('-').map(Number);
    const endParts = filterEndDate.split('-').map(Number);
    let currentDay = new Date(startParts[0], startParts[1] - 1, startParts[2]);
    const endDay = new Date(endParts[0], endParts[1] - 1, endParts[2]);
    
    while (currentDay <= endDay) {
      allDates.push(fmtDate(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    const days = allDates

    // Инициализируем и проставляем равномерные расходы
    days.forEach(d => {
      if (!dailyMap[d]) dailyMap[d] = { date: d, income: 0, expense: 0, details: { incomes: [], expenses: [] } }
      
      const dateObj = new Date(d + 'T00:00:00');
      const daysInMonth = new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0).getDate();
      
      const cutoffDateStr = useEstimatedRevenue ? filterEndDate : (maxIncomeDateStr || filterStartDate);
      if (maxIncomeDateStr && d <= cutoffDateStr) {
        localActivePlans.forEach(p => {
          const amt = Number(p.amount || p.monthly_amount || 0);
          const dailyAmt = isNaN(amt) ? 0 : amt / daysInMonth;
          if (dailyAmt > 0) {
            dailyMap[d].expense += dailyAmt;
            dailyMap[d].details.expenses.push({ 
              name: `Пост. расход: ${p.category || p.title || 'Регулярный платёж'}`, 
              amount: dailyAmt 
            });
          }
        });
      }
    })

    if (useEstimatedRevenue) {
      // 1. Начисление прогнозируемого (или фактического) дохода WB по дням
      days.forEach(d => {
        const dayFin = dailyFinancials.dailyData[d];
        if (dayFin && dailyMap[d]) {
          const inc = dayFin.income;
          dailyMap[d].income += inc;
          
          const label = dayFin.type === 'forecast' 
            ? `ВБ (Прогноз: ${(dayFin.tariffPercent).toFixed(2)}%)` 
            : `ВБ (Факт: ${(dayFin.tariffPercent).toFixed(2)}%)`;
          dailyMap[d].details.incomes.push({ name: label, amount: inc });
          
          const tax = inc * 0.06;
          dailyMap[d].expense += tax;
          dailyMap[d].details.expenses.push({ name: 'Налог УСН 6%', amount: tax });
        }
      });
      
      // 2. Добавление прочих доходов по дням
      otherIncomeList.forEach(i => {
        const inc = Number(i.amount||0);
        if (dailyMap[i.date]) {
          dailyMap[i.date].income += inc;
          dailyMap[i.date].details.incomes.push({ name: i.source || 'Прочий доход', amount: inc });
          
          const tax = inc * 0.06;
          dailyMap[i.date].expense += tax;
          dailyMap[i.date].details.expenses.push({ name: 'Налог УСН 6%', amount: tax });
        }
      });
    } else {
      // Режим кассы: берем плавный факт WB из dailyFinancials + прочие доходы по дням
      days.forEach(d => {
        const dayFin = dailyFinancials.dailyData[d];
        if (dayFin && dayFin.type === 'fact' && dailyMap[d]) {
          const inc = dayFin.income;
          if (inc > 0) {
            dailyMap[d].income += inc;
            dailyMap[d].details.incomes.push({ 
              name: `ВБ (Факт: ${(dayFin.tariffPercent).toFixed(2)}%)`, 
              amount: inc 
            });
            const tax = inc * 0.06;
            dailyMap[d].expense += tax;
            dailyMap[d].details.expenses.push({ name: 'Налог УСН 6%', amount: tax });
          }
        }
      });
      
      otherIncomeList.forEach(i => {
        const inc = Number(i.amount||0);
        if (dailyMap[i.date]) {
          dailyMap[i.date].income += inc;
          dailyMap[i.date].details.incomes.push({ name: i.source || 'Прочий доход', amount: inc });
          
          const tax = inc * 0.06;
          dailyMap[i.date].expense += tax;
          dailyMap[i.date].details.expenses.push({ name: 'Налог УСН 6%', amount: tax });
        }
      });
    }

    selectedPeriodExpenses.filter(e => !e.fixed_plan_id && e.is_paid !== false).forEach(e => {
      if (dailyMap[e.date]) {
        dailyMap[e.date].expense += Number(e.amount) || 0
        dailyMap[e.date].details.expenses.push({ name: e.category || 'Расход', amount: Number(e.amount) || 0 });
      }
    })
    reserves.forEach(r => {
      const d = r.date || r.created_at?.split('T')[0] || todayIso()
      if (dailyMap[d]) {
        dailyMap[d].reserve = (dailyMap[d].reserve || 0) + (Number(r.amount) || 0)
        dailyMap[d].details.reserves = dailyMap[d].details.reserves || []
        dailyMap[d].details.reserves.push({ name: 'Ручной резерв', amount: Number(r.amount) || 0 })
      }
    })
    // Партнёрские комиссии по дням
    selectedPeriodPartnerTurnovers.forEach(t => {
      const d = t.entry_date?.split('T')[0]
      if (dailyMap[d]) {
        dailyMap[d].expense += Number(t.commission_amount) || 0
        const partnerName = partners.find(p => p.id === t.partner_id)?.name || 'Не указан'
        dailyMap[d].details.expenses.push({ name: `Партнёр: ${partnerName}`, amount: Number(t.commission_amount) || 0 })
      }
    })
    
    // Начисление прогнозируемых расходов (ФОТ, Переменные, Партнеры) на будущие дни
    if (useEstimatedRevenue && dailyFinancials.lastActualDate) {
      days.forEach(d => {
        if (d > dailyFinancials.lastActualDate && dailyMap[d]) {
          if (averageDailyPayrollPast30 > 0) {
            dailyMap[d].expense += averageDailyPayrollPast30;
            dailyMap[d].details.expenses.push({ name: 'ФОТ (Прогноз)', amount: averageDailyPayrollPast30 });
          }
          if (averageDailyVarExpensesPast30 > 0) {
            dailyMap[d].expense += averageDailyVarExpensesPast30;
            dailyMap[d].details.expenses.push({ name: 'Перем. расходы (Прогноз)', amount: averageDailyVarExpensesPast30 });
          }
          if (averageDailyPartnerCommissionPast30 > 0) {
            dailyMap[d].expense += averageDailyPartnerCommissionPast30;
            dailyMap[d].details.expenses.push({ name: 'Партнёрская комиссия (Прогноз)', amount: averageDailyPartnerCommissionPast30 });
          }
        }
      });
    }

    // ФОТ по дням с нормализацией под payrollTotal
    const rawDailyPayroll = {}
    days.forEach(d => { rawDailyPayroll[d] = 0 })
    let rawDailyPayrollSum = 0

    salaryList.forEach(sal => {
      if (sal.dailyDetails) {
        Object.entries(sal.dailyDetails).forEach(([date, dayData]) => {
          if (rawDailyPayroll[date] !== undefined) {
            const dayTotal = Number(dayData?.total) || 0
            if (dayTotal > 0) {
              rawDailyPayroll[date] += dayTotal
              rawDailyPayrollSum += dayTotal
            }
          }
        })
      }
    })

    if (payrollTotal > 0) {
      if (rawDailyPayrollSum > 0) {
        const ratio = payrollTotal / rawDailyPayrollSum
        let allocatedPayroll = 0
        const daysWithShifts = days.filter(d => rawDailyPayroll[d] > 0)
        days.forEach(d => {
          if (rawDailyPayroll[d] > 0) {
            const isLast = (d === daysWithShifts[daysWithShifts.length - 1])
            const val = isLast ? Math.max(0, payrollTotal - allocatedPayroll) : rawDailyPayroll[d] * ratio
            allocatedPayroll += val
            dailyMap[d].expense += val
            dailyMap[d].details.expenses.push({ name: 'ФОТ (Зарплаты)', amount: val })
          }
        })
      } else {
        const perDay = payrollTotal / days.length
        let allocatedPayroll = 0
        days.forEach((d, idx) => {
          const val = (idx === days.length - 1) ? Math.max(0, payrollTotal - allocatedPayroll) : perDay
          allocatedPayroll += val
          dailyMap[d].expense += val
          dailyMap[d].details.expenses.push({ name: 'ФОТ (Зарплаты)', amount: val })
        })
      }
    }

    // Вычисляем ежедневную прибыль
    const flowChart = days.map(d => {
      const inc = Number(dailyMap[d].income) || 0
      const exp = Number(dailyMap[d].expense) || 0
      const actualProfit = inc - exp;
      
      const dateParts = d.split('-').map(Number);
      const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
      const daysOfWeek = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
      const dayName = daysOfWeek[dateObj.getDay()];

      return { 
        ...dailyMap[d],
        income: inc,
        expense: exp,
        profit: Math.max(0, actualProfit),
        realProfit: actualProfit,
        displayDate: `${d.split('-').slice(1).reverse().join('.')} ${dayName}`
      }
    })

    // Структура расходов для PieChart
    const localPayrollTotal = baseSalaryAccrued || 0
    const localVarExpenses = selectedPeriodExpenses.filter(e => e.type !== 'fixed_payment' && e.is_paid !== false).reduce((sum, e) => sum + (Number(e.amount) || 0), 0)
    
    const localTotalIncome = useEstimatedRevenue 
      ? Object.values(dailyFinancials.dailyData).reduce((sum, day) => sum + day.income, 0) + otherIncomeList.reduce((sum, i) => sum + Number(i.amount||0), 0)
      : displayedIncomes.reduce((sum, i) => sum + (Number(i.amount||0) + Number(i.compensation||0) + Number(i.bonus||0) - Number(i.penalty||0)), 0)

    const localTaxAmount = localTotalIncome * 0.06
    const localActualReserves = reserves.reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
    
    // Smart proration restricted to latest income date for the PieChart
    let localFixedPaid = 0
    if (maxIncomeDateStr) {
      localActivePlans.forEach(p => {
        const amount = Number(p.amount || p.monthly_amount || 0);
        if (amount <= 0 || isNaN(amount)) return;
        const pStart = new Date(filterStartDate + 'T00:00:00');
        const pEnd = new Date(filterEndDate + 'T23:59:59');
        const cutoffEnd = new Date(Math.min(pEnd.getTime(), new Date(maxIncomeDateStr + 'T23:59:59').getTime()));
        if (pStart > cutoffEnd) return;
        let cur = new Date(pStart.getFullYear(), pStart.getMonth(), 1);
        while (cur <= cutoffEnd) {
          const mYear = cur.getFullYear(), mMonth = cur.getMonth();
          const dim = new Date(mYear, mMonth + 1, 0).getDate();
          const mStart = new Date(mYear, mMonth, 1);
          const mEnd = new Date(mYear, mMonth + 1, 0, 23, 59, 59);
          const overlapStart = Math.max(mStart.getTime(), pStart.getTime());
          const overlapEnd = Math.min(mEnd.getTime(), cutoffEnd.getTime());
          if (overlapStart <= overlapEnd) {
            localFixedPaid += (amount / dim) * Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24));
          }
          cur.setMonth(cur.getMonth() + 1);
        }
      })
    }

    const expenseStruct = []
    if (localPayrollTotal > 0) expenseStruct.push({ name: 'ФОТ', value: localPayrollTotal, color: '#F59E0B' })
    if (localVarExpenses > 0) expenseStruct.push({ name: 'Переменные', value: localVarExpenses, color: '#EF4444' })
    if (localFixedPaid > 0) expenseStruct.push({ name: 'Постоянные', value: localFixedPaid, color: '#10B981' })

    if (localTaxAmount > 0) expenseStruct.push({ name: 'Налог УСН 6%', value: localTaxAmount, color: '#3B82F6' })

    const localPartnerCommission = selectedPeriodPartnerTurnovers.reduce((sum, t) => sum + (Number(t.commission_amount) || 0), 0)
    if (localPartnerCommission > 0) expenseStruct.push({ name: 'Партнёры', value: localPartnerCommission, color: '#8B5CF6' })

    return { flowChart, expenseStruct }
  }, [displayedIncomes, dailyFinancials, useEstimatedRevenue, otherIncomeList, expenses, reserves, fixedPlans, salaryList, baseSalaryAccrued, filterStartDate, filterEndDate, partnerTurnovers, partners, selectedPeriodExpenses, selectedPeriodPartnerTurnovers, averageDailyPayrollPast30, averageDailyVarExpensesPast30, averageDailyPartnerCommissionPast30])

  const currentLoadedMetrics = useMemo(() => {
    const profitMarginPct = totalIncome > 0 ? (actualFreeCashFlow / totalIncome) * 100 : 0
    return {
      totalIncome,
      totalWbIncome,
      totalOtherIncome,
      totalExpensesAll,
      payrollTotal,
      variableExpenses,
      fixedPaidExpenses,
      fixedPlanTotal,
      taxAmount,
      totalPartnerCommission,
      actualFreeCashFlow,
      profitMarginPct,
      pctPayroll,
      pctFixed,
      pctVar,
      pctTax,
      pctPartner,
      days: chartData?.flowChart ? chartData.flowChart.map(f => f.date) : [],
      dailyData: dailyFinancials.dailyData,
      flowChart: chartData.flowChart,
      expenseStruct: chartData.expenseStruct,
      hasAnyData,
      displayedIncomes,
      selectedPeriodExpenses,
      selectedPeriodPartnerTurnovers,
      salaryList,
      activePlans,
      lastActualDate: dailyFinancials.lastActualDate
    }
  }, [
    totalIncome, totalWbIncome, totalOtherIncome, totalExpensesAll,
    payrollTotal, variableExpenses, fixedPaidExpenses, fixedPlanTotal,
    taxAmount, totalPartnerCommission, actualFreeCashFlow,
    pctPayroll, pctFixed, pctVar, pctTax, pctPartner,
    dailyFinancials, chartData, hasAnyData,
    displayedIncomes, selectedPeriodExpenses, selectedPeriodPartnerTurnovers,
    salaryList, activePlans
  ])

  const memoizedPieChart = useMemo(() => {
    if (chartData.expenseStruct.length <= 1) return null
    return (
      <div className="analytics-card chart-card">
        <h3>Структура расходов</h3>
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} style={{ outline: 'none' }}>
            <PieChart style={{ outline: 'none' }}>
              <Pie 
                data={chartData.expenseStruct} 
                dataKey="value" 
                nameKey="name" 
                cx="50%" 
                cy="50%" 
                outerRadius={80} 
                innerRadius={50} 
                label={false}
                isAnimationActive={true}
                cornerRadius={6}
                paddingAngle={4}
              >
                {chartData.expenseStruct.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
              </Pie>
              <RechartsTooltip content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                const item = payload[0]
                const pctOfRevenue = totalIncome > 0 ? ((item.value / totalIncome) * 100).toFixed(1) : 0
                return (
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--surface-border)', borderRadius: '12px', padding: '12px 16px', boxShadow: '0 10px 25px rgba(0,0,0,0.08)', backdropFilter: 'blur(8px)', fontSize: '13px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '14px', color: 'var(--text-primary)' }}>{item.name}</div>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--orange)', marginBottom: '6px' }}>{formatMoney(item.value)}</div>
                    <div style={{ color: 'var(--wb-primary)', fontWeight: 600 }}>{pctOfRevenue}% от выручки</div>
                  </div>
                )
              }} />
              <Legend verticalAlign="bottom" height={50} formatter={(value) => {
                const entry = chartData.expenseStruct.find(e => e.name === value)
                const pctRev = entry && totalIncome > 0 ? ((entry.value / totalIncome) * 100).toFixed(0) : 0
                return `${value} (${pctRev}% выр.)`
              }}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    )
  }, [chartData.expenseStruct, totalIncome])

  const memoizedFlowChart = useMemo(() => {
    if (chartData.flowChart.length <= 1) return null
    return (
      <div className={`analytics-card chart-card ${chartData.expenseStruct.length <= 1 ? 'full-width' : ''}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
          <h3 style={{ margin: 0 }}>Динамика прибыли по дням <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: 400 }}>нажмите на столбец для детализации</span></h3>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', fontSize: '13px', background: 'var(--bg-secondary)', padding: '6px 14px', borderRadius: '10px', border: '1px solid var(--surface-border)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}>
              <input type="checkbox" checked={visibleFinMetrics.income} onChange={(e) => setVisibleFinMetrics({ ...visibleFinMetrics, income: e.target.checked })} style={{ accentColor: 'var(--success)' }} />
              <span style={{width:8,height:8,borderRadius:'50%',display:'inline-block',marginRight:4}} style={{...({width:8,height:8,borderRadius:'50%',display:'inline-block',marginRight:4}),background:'var(--success)'}}/> Доходы
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}>
              <input type="checkbox" checked={visibleFinMetrics.expense} onChange={(e) => setVisibleFinMetrics({ ...visibleFinMetrics, expense: e.target.checked })} style={{ accentColor: 'var(--danger)' }} />
              <span style={{width:8,height:8,borderRadius:'50%',display:'inline-block',background:'var(--danger)'}}/> Расходы
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none', fontWeight: 500 }}>
              <input type="checkbox" checked={visibleFinMetrics.profit} onChange={(e) => setVisibleFinMetrics({ ...visibleFinMetrics, profit: e.target.checked })} style={{ accentColor: 'var(--wb-primary)' }} />
              <span style={{width:8,height:8,borderRadius:'50%',display:'inline-block',background:'var(--wb-primary)'}}/> Прибыль
            </label>
          </div>
        </div>
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} style={{ outline: 'none' }}>
            <BarChart 
              data={chartData.flowChart} 
              onClick={(e) => { 
                if (e && e.activeTooltipIndex !== undefined) {
                  const dayData = chartData.flowChart[e.activeTooltipIndex];
                  if (dayData) setDayDetailsData(dayData);
                }
              }} 
              style={{ cursor: 'pointer', outline: 'none' }}
            >
              <CartesianGrid vertical={false} stroke="var(--surface-border)" strokeDasharray="3 3" />
              <XAxis 
                dataKey="displayDate" 
                tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} 
                axisLine={false} 
                tickLine={{ stroke: 'var(--surface-border)', strokeWidth: 1 }} 
                angle={-45}
                textAnchor="end"
                height={50}
              />
              <YAxis tickFormatter={(val) => `${val/1000}k`} tick={{fontSize: 11, fill: 'var(--text-secondary)'}} axisLine={false} tickLine={false} />
              <RechartsTooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null
                const data = payload[0]?.payload
                if (!data) return null
                const expenseGroups = {}
                ;(data.details?.expenses || []).forEach(e => {
                  const cat = e.name.startsWith('ФОТ:') ? 'ФОТ' : e.name
                  expenseGroups[cat] = (expenseGroups[cat] || 0) + e.amount
                })
                return (
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--surface-border)', borderRadius: '12px', padding: '12px 16px', boxShadow: '0 10px 25px rgba(0,0,0,0.08)', backdropFilter: 'blur(8px)', fontSize: '13px', maxWidth: '260px' }}>
                    <div style={{ fontWeight: 700, marginBottom: '8px', fontSize: '14px', color: 'var(--text-primary)' }}>{label}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ color: 'var(--success)' }}>Доходы</span>
                      <span style={{ fontWeight: 600, color: 'var(--success)' }}>{formatMoney(data.income)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                      <span style={{ color: 'var(--danger)' }}>Расходы</span>
                      <span style={{ fontWeight: 600, color: 'var(--danger)' }}>{formatMoney(data.expense)}</span>
                    </div>
                    {Object.keys(expenseGroups).length > 0 && (
                      <div style={{ paddingLeft: '10px', borderLeft: '2px solid var(--danger)', margin: '4px 0 6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {Object.entries(expenseGroups).map(([name, amt]) => (
                          <div key={name} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '1px 0' }}>
                            <span>{name}</span>
                            <span style={{ whiteSpace: 'nowrap' }}>{formatMoney(amt)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--surface-border)', paddingTop: '4px' }}>
                      <span style={{ color: 'var(--wb-primary)', fontWeight: 600 }}>Прибыль</span>
                      <span style={{ fontWeight: 700, color: data.realProfit < 0 ? 'var(--danger)' : 'var(--wb-primary)' }}>{formatMoney(data.realProfit)}</span>
                    </div>
                  </div>
                )
              }} />
              <Legend verticalAlign="top" height={36} />
              {visibleFinMetrics.income && <Bar dataKey="income" name="Доходы" fill="var(--success)" radius={[6, 6, 0, 0]} isAnimationActive={true} />}
              {visibleFinMetrics.expense && <Bar dataKey="expense" name="Расходы" fill="var(--danger)" radius={[6, 6, 0, 0]} isAnimationActive={true} />}
              {visibleFinMetrics.profit && <Bar dataKey="profit" name="Прибыль (За день)" fill="var(--wb-primary)" radius={[6, 6, 0, 0]} isAnimationActive={true} />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    )
  }, [chartData.flowChart, chartData.expenseStruct, visibleFinMetrics, setDayDetailsData])

  if (loading && !hasAnyData && payrollTotal === 0) {
    return <div className="analytics-page animate-fade-in"><div className="analytics-container"><SkeletonGrid cols={4} rows={2} height="140px" /></div></div>
  }

  return (
    <div className="analytics-page">
      <div className="analytics-container">
        
        {/* ВЕРХНЯЯ ЗОНА */}
        <div className="analytics-header">
          <div className="analytics-header-titles">
            <h1>Аналитика</h1>
            <p>Доходы, расходы, ФОТ и обязательства ПВЗ</p>
          </div>
          <div className="analytics-header-actions">
            <DateRangeFilter
              startDate={filterStartDate} endDate={filterEndDate}
              onApply={(start, end) => { setFilterStartDate(start); setFilterEndDate(end); }}
              onReset={() => { setFilterStartDate(monthStartIso()); setFilterEndDate(monthEndIso()); }}
              quickPresets={[
                { label: 'Этот месяц', getValue: () => [monthStartIso(), monthEndIso()] },
                { label: 'Прошлый месяц', getValue: () => {
                  const d = new Date()
                  const start = new Date(d.getFullYear(), d.getMonth() - 1, 1)
                  const end = new Date(d.getFullYear(), d.getMonth(), 0)
                  return [
                    `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-01`,
                    `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
                  ]
                }},
                { label: 'Этот квартал', getValue: () => {
                  const d = new Date();
                  const q = Math.floor(d.getMonth() / 3);
                  const start = new Date(d.getFullYear(), q * 3, 1);
                  const end = new Date(d.getFullYear(), q * 3 + 3, 0);
                  return [
                    `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-01`,
                    `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
                  ];
                }}
              ]}
              showQuickButtons
            />
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div className="accrued-toggle-container">
                <button className={`accrued-toggle-btn ${useEstimatedRevenue ? 'active' : ''}`} onClick={() => setUseEstimatedRevenue(true)}>
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/></svg>
                  Прогноз
                </button>
                <button className={`accrued-toggle-btn ${!useEstimatedRevenue ? 'active' : ''}`} onClick={() => setUseEstimatedRevenue(false)}>
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                  Касса
                </button>
              </div>
              {useEstimatedRevenue && (
                <button 
                  className={`btn-icon-only ${showForecastSettings ? 'active' : ''}`}
                  onClick={() => setShowForecastSettings(!showForecastSettings)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '36px', height: '36px', borderRadius: '10px',
                    border: '1px solid var(--surface-border)',
                    background: showForecastSettings ? 'rgba(140, 80, 255, 0.1)' : 'var(--bg-primary)',
                    color: showForecastSettings ? 'var(--wb-primary)' : 'var(--text-secondary)',
                    cursor: 'pointer', transition: 'all 0.2s ease',
                    padding: 0
                  }}
                  title="Настройки моделирования прогноза"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: showForecastSettings ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                </button>
              )}
            </div>
            <button
              className="btn btn-secondary btn-export-analytics"
              onClick={() => setShowExportModal(true)}
              title="Экспорт отчёта в PDF или Excel"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span>Экспорт</span>
            </button>
            <button className="btn btn-primary btn-add-main" data-tour="analytics-add" onClick={() => openModal('action_chooser')}>
              + Добавить
            </button>
          </div>
        </div>

        {/* ГЛАВНЫЙ УПРАВЛЕНЧЕСКИЙ ВЫВОД */}
        {hasAnyData && totalIncome === 0 && payrollTotal > 0 && (
          <div className="analytics-summary-banner">
            <div className="banner-content warning">
              <div className="banner-text">
                <strong>За выбранный период есть ФОТ, но нет поступлений.</strong>
                <p>Поэтому фактический свободный остаток сейчас отрицательный.</p>
              </div>
              <button className="btn btn-outline-danger" onClick={() => openModal('income')}>Добавить поступление WB</button>
            </div>
          </div>
        )}
        
        {hasAnyData && totalIncome > 0 && (
          <div className="analytics-summary-banner">
            <div className="banner-content success">
              <div className="banner-text">
                <p>За период пришло <b>{formatMoney(totalIncome)}</b>, после расходов и ФОТ осталось <b>{formatMoney(actualFreeCashFlow)}</b>.</p>
              </div>
            </div>
          </div>
        )}

        {hasAnyData && !maxIncomeDateStr && (
          <div className="analytics-summary-banner">
            <div 
              className={`banner-content info collapsible ${isNoticeExpanded ? 'expanded' : ''}`}
              onClick={() => setIsNoticeExpanded(!isNoticeExpanded)}
              style={{ cursor: 'pointer', transition: 'all 0.3s ease', flexDirection: 'column', alignItems: 'flex-start', padding: '14px 20px', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="banner-icon-container" style={{ width: '32px', height: '32px', marginRight: 0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="9" x2="12.01" y2="9"/></svg>
                  </div>
                  <strong style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>Период планирования (расходы временно скрыты)</strong>
                </div>
                <div style={{ transform: isNoticeExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', display: 'flex', alignItems: 'center', color: 'var(--text-secondary)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>
              
              <motion.div 
                initial={false}
                animate={{ 
                  height: isNoticeExpanded ? 'auto' : 0, 
                  opacity: isNoticeExpanded ? 1 : 0, 
                  marginTop: isNoticeExpanded ? 12 : 0 
                }}
                transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                style={{ width: '100%', overflow: 'hidden' }}
              >
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', borderTop: '1px solid var(--surface-border)', paddingTop: '12px' }}>
                  Постоянные расходы временно скрыты, так как за выбранный период еще нет начислений доходов. Как только вы добавите первую выручку, расходы автоматически начислятся до этого дня.
                </p>
              </motion.div>
            </div>
          </div>
        )}

        {hasAnyData && maxIncomeDateStr && maxIncomeDateStr < filterEndDate && (
          <div className="analytics-summary-banner">
            <div 
              className={`banner-content info collapsible ${isNoticeExpanded ? 'expanded' : ''}`}
              onClick={() => setIsNoticeExpanded(!isNoticeExpanded)}
              style={{ cursor: 'pointer', transition: 'all 0.3s ease', flexDirection: 'column', alignItems: 'flex-start', padding: '14px 20px', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="banner-icon-container" style={{ width: '32px', height: '32px', marginRight: 0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="9" x2="12.01" y2="9"/></svg>
                  </div>
                  <strong style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                    Динамический учет расходов (начислено по {maxIncomeDateStr.split('-').reverse().join('.')})
                  </strong>
                </div>
                <div style={{ transform: isNoticeExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', display: 'flex', alignItems: 'center', color: 'var(--text-secondary)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>
              
              <motion.div 
                initial={false}
                animate={{ 
                  height: isNoticeExpanded ? 'auto' : 0, 
                  opacity: isNoticeExpanded ? 1 : 0, 
                  marginTop: isNoticeExpanded ? 12 : 0 
                }}
                transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                style={{ width: '100%', overflow: 'hidden' }}
              >
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', borderTop: '1px solid var(--surface-border)', paddingTop: '12px' }}>
                  Постоянные расходы автоматически начислены по {maxIncomeDateStr.split('-').reverse().join('.')} (дата последнего добавленного дохода). Это позволяет вам видеть точную чистую прибыль на момент последних поступлений.
                </p>
              </motion.div>
            </div>
          </div>
        )}

      {/* Панель настроек моделирования прогноза */}
      <AnimatePresence>
        {showForecastSettings && useEstimatedRevenue && (
          <motion.div
            initial={{ height: 0, opacity: 0, marginBottom: 0 }}
            animate={{ height: 'auto', opacity: 1, marginBottom: 24 }}
            exit={{ height: 0, opacity: 0, marginBottom: 0 }}
            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            style={{ overflow: 'hidden', width: '100%' }}
          >
            <div 
              className="card" 
              style={{ 
                padding: '20px', 
                background: 'var(--surface)', 
                border: '1px solid var(--surface-border)', 
                borderRadius: '16px',
                boxShadow: 'var(--shadow-sm)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(140, 80, 255, 0.1)', color: 'var(--wb-primary)' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06-.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06-.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Настройки моделирования прогноза</h3>
                </div>
                <button 
                  onClick={() => setShowForecastSettings(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '4px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>

              <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                Прогноз доходов WB основывается на динамическом проценте от оборота (скользящие 30 дней) или на фиксированном проценте. Оборот вычисляется по реальным отчетам или прогнозируется по логистическим выдачам.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', alignItems: 'start' }}>
                
                {/* Левая колонка: Выбор режима */}
                <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--surface-border)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>Режим расчета тарифа прогноза</div>
                  <div style={{ display: 'flex', gap: '8px', background: 'var(--bg-primary)', padding: '4px', borderRadius: '10px', border: '1px solid var(--surface-border)', marginBottom: '16px' }}>
                    <button 
                      type="button"
                      onClick={() => handleForecastSettingUpdate(false, forecastCalcPeriod, forecastManualRate)}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                        background: !forecastAutoCalc ? 'var(--wb-primary)' : 'transparent',
                        color: !forecastAutoCalc ? 'white' : 'var(--text-secondary)'
                      }}
                    >
                      Фикс. %
                    </button>
                    <button 
                      type="button"
                      onClick={() => handleForecastSettingUpdate(true, forecastCalcPeriod, forecastManualRate)}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                        background: forecastAutoCalc ? 'var(--wb-primary)' : 'transparent',
                        color: forecastAutoCalc ? 'white' : 'var(--text-secondary)'
                      }}
                    >
                      Динамический %
                    </button>
                  </div>

                  {!forecastAutoCalc ? (
                    <div className="animate-fade-in">
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Процент тарифа от оборота</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="salary-config-input-wrapper" style={{ flex: 1, margin: 0 }}>
                          <input 
                            type="number" 
                            className="input" 
                            value={forecastManualRate} 
                            min="0"
                            max="100"
                            step="0.01"
                            onChange={(e) => setForecastManualRate(e.target.value)}
                            placeholder="3.0"
                            style={{ width: '100%' }}
                          />
                          <span className="salary-config-unit">%</span>
                        </div>
                        <button 
                          type="button"
                          className="btn btn-primary"
                          disabled={isForecastSaving}
                          onClick={() => handleForecastSettingUpdate(false, forecastCalcPeriod, forecastManualRate)}
                          style={{ height: '40px', padding: '0 16px', borderRadius: '10px' }}
                        >
                          {isForecastSaving ? '...' : 'Применить'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="animate-fade-in">
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Автоматический расчет</label>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success)' }}></span>
                        Динамический скользящий тариф
                      </div>
                      <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '8px', lineHeight: '1.4', margin: '8px 0 0 0' }}>
                        Использует формулу WB на основе оборота за 30 дней. Автоматически подстраивается под новые отчеты.
                      </p>
                    </div>
                  )}
                </div>

                {/* Правая колонка: Детали расчета */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--surface-border)' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
                      {forecastAutoCalc ? 'Текущие показатели динамического тарифа' : 'Показатели динамического тарифа (для справки)'}
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px dashed var(--surface-border)', paddingBottom: '6px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Оборот за 30 дней:</span>
                        <span style={{ fontWeight: 600 }}>{formatMoney(currentRollingTurnover)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px dashed var(--surface-border)', paddingBottom: '6px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Среднесуточный оборот:</span>
                        <span style={{ fontWeight: 600 }}>{formatMoney(currentRollingTurnover / 30)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderBottom: '1px dashed var(--surface-border)', paddingBottom: '6px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Текущий процент тарифа:</span>
                        <span style={{ fontWeight: 700, color: 'var(--wb-primary)' }}>{currentForecastTariff.toFixed(3)} %</span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: '1.3', marginTop: '4px' }}>
                        Формула: 3.885% − (Оборот за 30 дней / 9 000 000)<br />
                        Процент ограничен рамками от 1.5% до 4.5% согласно правилам Wildberries.
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showImportWizard && (
          <ImportWizard
            isOpen={showImportWizard}
            onClose={() => setShowImportWizard(false)}
            employees={[]}
            onComplete={() => loadData(true)}
            targetTable="wb_incomes"
            fields={[
              { id: 'period_start', label: 'Начало периода', required: true },
              { id: 'period_end', label: 'Конец периода', required: true },
              { id: 'amount', label: 'Сумма к перечислению (Итого к оплате)', required: true },
              { id: 'turnover', label: 'Оборот в рублях', required: false },
              { id: 'sale_by_tariff', label: 'Продажа по тарифу', required: false },
              { id: 'bonus', label: 'Премия', required: false },
              { id: 'penalty', label: 'Удержания', required: false }
            ]}
            pvzId={activePvz.id}
          />
        )}

        {/* ONBOARDING ПУСТОЙ АНАЛИТИКИ */}
        {!hasAnyData && payrollTotal === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="analytics-onboarding">
            <h2>Настройте аналитику за 3 шага</h2>
            <div className="onboarding-steps">
              <div className="onboarding-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>Добавьте поступление WB</h4>
                  <p>Чтобы видеть, сколько денег пришло за период.</p>
                  <button className="btn btn-primary" onClick={() => openModal('income')}>+ Поступление WB</button>
                </div>
              </div>
              <div className="onboarding-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>Добавьте постоянные расходы</h4>
                  <p>Аренда, коммуналка, интернет и другие обязательства.</p>
                  <button className="btn btn-secondary" onClick={() => openModal('plan')}>+ Постоянный расход</button>
                </div>
              </div>
              <div className="onboarding-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>Добавьте переменные расходы</h4>
                  <p>Разовые траты: ремонт, расходники, бытовые покупки.</p>
                  <button className="btn btn-secondary" onClick={() => openModal('expense_var')}>+ Переменный расход</button>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            
            {/* БЛОК KPI (3 КОЛОНКИ НА ДЕСКТОПЕ) */}
            <div className="analytics-grid-kpi main-kpi-row" data-tour="analytics-chart">
              <div className="kpi-card interactive" onClick={() => { setActiveTab('incomes'); setRegistryModal(true) }}>
                <div className="kpi-title">Выручка (Доходы)</div>
                <div className="kpi-value text-green">{formatMoney(totalIncome)}</div>
                <div className="kpi-hint">Все поступления за выбранный период</div>
              </div>
              
              <div className="kpi-card interactive" onClick={() => setShowExpensesDetails(true)}>
                <div className="kpi-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 0 }}>
                  <span>Расходы (Всего)</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', background: 'var(--bg-secondary)', padding: '2px 8px', borderRadius: '8px', fontWeight: 500 }}>Подробнее</span>
                </div>
                <div className="kpi-value text-red">{formatMoney(totalExpensesAll)}</div>
                <div className="kpi-hint" style={{ marginTop: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    <span>Структура затрат</span>
                    <span>{totalIncome > 0 ? ((totalExpensesAll / totalIncome) * 100).toFixed(0) : 0}% от выр.</span>
                  </div>
                  {/* Цветная сегментированная полоса */}
                  <div className="mini-segments-bar" style={{ display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', background: 'var(--surface-border)' }}>
                    {pctPayroll > 0 && <div style={{ width: `${pctPayroll}%`, background: '#F59E0B' }} title={`ФОТ: ${formatMoney(payrollTotal)}`} />}
                    {pctFixed > 0 && <div style={{ width: `${pctFixed}%`, background: '#10B981' }} title={`Постоянные: ${formatMoney(fixedPaidExpenses)}`} />}
                    {pctVar > 0 && <div style={{ width: `${pctVar}%`, background: '#EF4444' }} title={`Переменные: ${formatMoney(variableExpenses)}`} />}
                    {pctTax > 0 && <div style={{ width: `${pctTax}%`, background: '#3B82F6' }} title={`Налоги: ${formatMoney(taxAmount)}`} />}
                    {pctPartner > 0 && <div style={{ width: `${pctPartner}%`, background: '#8B5CF6' }} title={`Партнёры: ${formatMoney(totalPartnerCommission)}`} />}
                  </div>
                </div>
              </div>

              <div className={`kpi-card ${actualFreeCashFlow < 0 ? 'kpi-negative' : 'kpi-positive'}`}>
                <div className="kpi-title">Чистая прибыль</div>
                <div className={`kpi-value ${actualFreeCashFlow < 0 ? 'text-red' : 'text-green'}`}>{formatMoney(actualFreeCashFlow)}</div>
                <div className="kpi-hint">Доходы − Все расходы (Рентабельность: {totalIncome > 0 ? ((actualFreeCashFlow / totalIncome) * 100).toFixed(0) : 0}%)</div>
              </div>
            </div>

            {/* P&L И ГРАФИКИ */}
            <div className="analytics-grid-main mt-6">
              
              {/* P&L Каскад */}
              <div className="analytics-card pl-card full-width" data-tour="analytics-pl">
                <div className="pl-header">
                  <h3>Фактический денежный поток</h3>
                  <p>Только реальные записи за выбранный период. Нажмите на строку для детализации.</p>
                </div>
                
                <div className="pl-container">
                  <div className="pl-list">
                    <div className="pl-row" onClick={() => { setActiveTab('incomes'); setRegistryModal(true) }}>
                      <span className="pl-label"><span style={{display:'inline-flex'}}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></span> Выручка</span>
                      <span className="pl-amount text-green">+ {formatMoney(totalIncome)}</span>
                    </div>
                    
                    <div className="pl-row" onClick={() => { setActiveTab('payroll'); setRegistryModal(true) }}>
                      <span className="pl-label text-secondary"><span style={{display:'inline-flex'}}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></span> ФОТ (Зарплаты)</span>
                      <span className="pl-amount text-orange">− {formatMoney(payrollTotal)}</span>
                    </div>
                    
                    <div className="pl-row" onClick={() => { setActiveTab('expenses'); setRegistryModal(true) }}>
                      <span className="pl-label text-secondary"><span style={{display:'inline-flex'}}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg></span> Переменные расходы</span>
                      <span className="pl-amount text-red">− {formatMoney(variableExpenses)}</span>
                    </div>
                    
                    <div className="pl-row" onClick={() => { setActiveTab('expenses'); setRegistryModal(true) }}>
                      <span className="pl-label text-secondary"><span style={{display:'inline-flex'}}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01"/></svg></span> Постоянные</span>
                      <span className="pl-amount text-red">− {formatMoney(fixedPaidExpenses)}</span>
                    </div>
                    

                    
                    {taxAmount > 0 && (
                      <div className="pl-row" onClick={() => { setActiveTab('incomes'); setRegistryModal(true) }}>
                        <span className="pl-label text-secondary"><span style={{display:'inline-flex'}}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg></span> Налог УСН (6%)</span>
                        <span className="pl-amount" style={{ color: '#3B82F6' }}>− {formatMoney(taxAmount)}</span>
                      </div>
                    )}
                    
                    {totalPartnerCommission > 0 && (
                      <div className="pl-row" onClick={() => { setShowExpensesDetails(true); setExpenseDetailsSubScreen('partners'); }} style={{ cursor: 'pointer' }}>
                        <span className="pl-label text-secondary"><span style={{display:'inline-flex'}}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></span> Расходы на партнёров</span>
                        <span className="pl-amount text-red">− {formatMoney(totalPartnerCommission)}</span>
                      </div>
                    )}
                  </div>
                  <div className="pl-total-box">
                    <span>Итог</span>
                    <span className={actualFreeCashFlow < 0 ? 'text-red' : 'text-green'}>{formatMoney(actualFreeCashFlow)}</span>
                  </div>
                </div>
              </div>

              {/* Графики - показываем только если есть данные */}
              {showCharts(chartData) && (
                <>
                  {memoizedPieChart}
                  {memoizedFlowChart}
                </>
              )}
            </div>

            {/* ПОСТОЯННЫЕ РАСХОДЫ */}
            <div className="analytics-section mt-6">
              <div className="analytics-section-header">
                <h2>Постоянные расходы (План)</h2>
                {activePlans.length > 0 && <button className="btn btn-ghost" onClick={() => openModal('plan')}>+ Добавить план</button>}
              </div>
              
              {activePlans.length === 0 ? (
                <div className="setup-card">
                  <div className="setup-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></div>
                  <div className="setup-content">
                    <h3>Постоянные расходы не настроены</h3>
                    <p>Добавьте аренду, коммуналку, интернет и другие регулярные платежи, чтобы видеть обязательства месяца.</p>
                    <div className="setup-examples">
                      <span>Аренда</span>
                      <span>Коммуналка</span>
                      <span>Интернет</span>
                      <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> Охрана</span>
                    </div>
                    <button className="btn btn-primary mt-4" onClick={() => openModal('plan')}>+ Добавить постоянный расход</button>
                  </div>
                </div>
              ) : (
                <div className="plans-grid">
                  {activePlans.map(plan => {
                    const planAmount = Number(plan.amount || plan.monthly_amount || 0);
                    const paid = expenses.filter(e => e.fixed_plan_id === plan.id).reduce((s, e) => s + Number(e.amount), 0);
                    
                    let manualReserved = reserves.filter(r => r.template_id === plan.id || r.fixed_plan_id === plan.id).reduce((s, r) => s + Number(r.amount), 0);
                    let reserved = manualReserved;
                    const isAuto = plan.is_auto_reserve === true;
                    
                    let autoReserved = 0;
                    if (isAuto) {
                       const d = new Date(filterEndDate);
                       const currentDay = d.getDate();
                       const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
                       const dueDay = Math.min(plan.due_day || daysInMonth, daysInMonth);
                       
                       if (currentDay >= dueDay) {
                         // Дата оплаты прошла — 100% должно быть собрано
                         autoReserved = planAmount;
                       } else {
                         // Умная модель: от реального дохода
                         // Какую долю от дохода нужно откладывать = planAmount / ожидаемый_доход_за_месяц
                         // Ожидаемый доход экстраполируется из текущего
                         const incomePerDay = currentDay > 0 ? totalIncome / currentDay : 0;
                         const expectedMonthlyIncome = incomePerDay * daysInMonth;
                         
                         if (expectedMonthlyIncome > 0) {
                           // Доля дохода на этот расход
                           const shareOfIncome = planAmount / expectedMonthlyIncome;
                           // Сколько уже нужно было собрать к сегодня (пропорция к due_day)
                           const progressToDeadline = Math.min(currentDay / dueDay, 1);
                           // Автосбор = доля дохода × фактический доход × прогресс к дедлайну
                           autoReserved = Math.min(planAmount, Math.round(totalIncome * shareOfIncome * (dueDay <= currentDay ? 1 : (currentDay / dueDay) * (dueDay / daysInMonth) + (1 - dueDay / daysInMonth))));
                           // Упрощённая формула: пропорция пройденных дней к дате оплаты
                           autoReserved = Math.min(planAmount, Math.round((planAmount / dueDay) * currentDay));
                         } else {
                           // Нет дохода — считаем по дням
                           autoReserved = Math.min(planAmount, Math.round((planAmount / dueDay) * currentDay));
                         }
                       }
                       reserved = Math.max(manualReserved, autoReserved);
                    }
                    
                    const remPay = Math.max(planAmount - paid, 0);
                    const remRes = Math.max(planAmount - reserved, 0);
                    const recWb = expectedWbPayments > 0 && (plan.accounting_method === 'wb_payment_reserve' || plan.accounting_method === 'monthly_even') ? Math.round(planAmount / expectedWbPayments) : 0;
                    
                    return (
                      <div key={plan.id} className="plan-card">
                        <div className="plan-card-header">
                          <strong className="plan-title">{plan.category || plan.title}</strong>
                          <span className="plan-amount">{formatMoney(planAmount)}</span>
                        </div>
                        <div className="plan-meta">
                          {isAuto ? (
                             <span className="text-purple" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg> Авто-сбор</span>
                          ) : (
                             <span>{plan.accounting_method === 'wb_payment_reserve' ? 'С выплат WB' : plan.accounting_method === 'fact_only' ? 'По факту' : 'Резерв'}</span>
                          )}
                          {plan.due_day && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> До {plan.due_day}-го числа</span>}
                          {isAuto && totalIncome > 0 && <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>{(planAmount / totalIncome * 100).toFixed(1)}% от дохода</span>}
                        </div>

                        <div className="plan-progress">
                          <div className="progress-labels">
                            <span>Оплачено: {formatMoney(paid)}</span>
                            <span className={remPay === 0 ? 'text-green' : 'text-red'}>Осталось: {formatMoney(remPay)}</span>
                          </div>
                          <div className="progress-bar-bg">
                            <div className="progress-bar-fill bg-green" style={{ width: `${Math.min((paid/planAmount)*100, 100)}%` }}></div>
                          </div>

                          <div className="progress-labels mt-2">
                            <span>{isAuto ? `Собрано: ${formatMoney(reserved)}` : `Отложено: ${formatMoney(reserved)}`}</span>
                            <span className="text-secondary">Осталось собрать: {formatMoney(remRes)}</span>
                          </div>
                          <div className="progress-bar-bg">
                            <div className="progress-bar-fill bg-purple" style={{ width: `${Math.min((reserved/planAmount)*100, 100)}%` }}></div>
                          </div>
                        </div>

                        {isAuto && totalIncome > 0 && (
                          <div className="plan-recommendation" style={{ background: 'var(--bg-secondary)', padding: '8px 12px', borderRadius: '8px', fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--purple)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                            <span>С каждого дохода откладывается ~{(planAmount / totalIncome * 100).toFixed(1)}%. К {plan.due_day || 'концу месяца'}-му числу будет собрана вся сумма.</span>
                          </div>
                        )}

                        {!isAuto && recWb > 0 && remRes > 0 && (
                          <div className="plan-recommendation">Рекомендация: откладывать {formatMoney(recWb)} с WB</div>
                        )}

                        <div className="plan-actions mt-3">
                          <button className="btn btn-outline-primary" onClick={() => openModal('expense_fixed', plan.id)}>Оплатить</button>
                          {!isAuto && <button className="btn btn-outline-secondary" onClick={() => openModal('reserve', plan.id)}>Отложить</button>}
                          <button className="btn-icon-delete" onClick={() => handleDelete('plan', plan.id)} title="Удалить план"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* ─── ПАРТНЁРСКИЕ ПРОГРАММЫ ─── */}
            {isOwner && (
              <div className="analytics-section mt-6">
                <div className="analytics-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Партнёрские программы</h3>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPartnerModal('add_partner')} style={{ color: 'var(--wb-primary)' }}>+ Добавить партнёра</button>
                </div>

                {partners.length === 0 ? (
                  <div style={{ background: 'var(--surface)', border: '1px solid var(--surface-border)', borderRadius: '16px', padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5" style={{ marginBottom: '12px' }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    <p style={{ margin: 0, fontSize: '14px' }}>Нет партнёров. Добавьте партнёра, чтобы учитывать комиссионные выплаты.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {partners.filter(p => p.is_active !== false).map(partner => {
                      const fDetails = partnerForecastDetails[partner.id] || { turnover: 0, commission: 0, pvzRevenue: 0 }
                      const entries = partnerTurnovers.filter(t => {
                        const d = t.entry_date?.split('T')[0]
                        return t.partner_id === partner.id && d >= filterStartDate && d <= filterEndDate
                      })
                      const actualTurnover = entries.reduce((s, e) => s + Number(e.turnover_amount), 0)
                      const actualCommission = entries.reduce((s, e) => s + Number(e.commission_amount), 0)

                      const actualPvzRevenue = entries.reduce((sum, e) => {
                        const dateStr = e.entry_date?.split('T')[0]
                        const dayData = dailyFinancials.dailyData[dateStr]
                        const dayTariff = dayData ? dayData.tariffPercent : currentForecastTariff
                        return sum + (Number(e.turnover_amount) * (dayTariff / 100))
                      }, 0)

                      const totalTurnover = actualTurnover + fDetails.turnover
                      const totalCommission = actualCommission + fDetails.commission
                      const totalPvzRevenue = actualPvzRevenue + fDetails.pvzRevenue
                      const pvzNetProfit = totalPvzRevenue - totalCommission

                      return (
                        <div key={partner.id} className="plan-card" style={{ padding: '24px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '28px' }}>
                            {/* ЛЕВАЯ КОЛОНКА: Сводная информация и действия */}
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '20px' }}>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 107, 0, 0.1)', color: 'var(--wb-primary)' }}>
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                                    </div>
                                    <div>
                                      <strong style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', display: 'block' }}>{partner.name}</strong>
                                      <span style={{ fontSize: '12px', color: 'var(--wb-primary)', fontWeight: '600', background: 'rgba(255, 107, 0, 0.08)', padding: '2px 8px', borderRadius: '6px', marginTop: '2px', display: 'inline-block' }}>Ставка: {partner.commission_percent}%</span>
                                    </div>
                                  </div>
                                  <button className="btn-icon-delete" onClick={async () => {
                                    if (!confirm(`Удалить партнёра "${partner.name}" и все записи?`)) return
                                    try { await api.deletePartner(partner.id); toast.success('Удалён'); loadData(true) } catch (e) { toast.error(e.message) }
                                  }} title="Удалить партнёра" style={{ padding: '8px', borderRadius: '8px' }}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                  </button>
                                </div>

                                {/* Статистика в виде плиток */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                                  <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--surface-border)', borderRadius: '12px', padding: '12px' }}>
                                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '4px' }}>Оборот партнёра</div>
                                    <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>{formatMoney(totalTurnover)}</div>
                                  </div>
                                  <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--surface-border)', borderRadius: '12px', padding: '12px' }}>
                                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '4px' }}>Записей за период</div>
                                    <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>{entries.length}</div>
                                  </div>

                                  <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--surface-border)', borderRadius: '12px', padding: '12px' }}>
                                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '4px' }}>Выплачено партнёру</div>
                                    <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--warning)' }}>{formatMoney(totalCommission)}</div>
                                  </div>
                                  <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--surface-border)', borderRadius: '12px', padding: '12px' }}>
                                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '4px' }}>Получено от WB</div>
                                    <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--success)' }}>{formatMoney(totalPvzRevenue)}</div>
                                  </div>

                                  <div style={{ gridColumn: 'span 2', background: 'rgba(139, 92, 246, 0.05)', border: '1px solid rgba(139, 92, 246, 0.15)', borderRadius: '12px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Выручка с партнёра (до налога и тарифа)</span>
                                    <span style={{ fontSize: '16px', fontWeight: 800, color: pvzNetProfit < 0 ? 'var(--danger)' : 'var(--wb-primary)' }}>
                                      {formatMoney(pvzNetProfit)}
                                    </span>
                                  </div>

                                  {(() => {
                                    const analysis = partnerMetricsAnalysis[partner.id] || { ownTurnover: 0, averageTariff: 0, averageTariffWithout: 0, tariffLoss: 0, taxAmount: 0, netBenefit: 0 };
                                    const tariffDiff = analysis.averageTariff - analysis.averageTariffWithout;
                                    return (
                                      <details style={{ gridColumn: 'span 2', background: 'var(--bg-secondary)', border: '1px solid var(--surface-border)', borderRadius: '12px', padding: '10px 14px', fontSize: '13px', transition: 'all 0.2s ease' }}>
                                        <summary style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}>
                                          <span>Финансовый анализ партнёрства (ROI)</span>
                                          <span style={{ fontSize: '12px', fontWeight: 700, color: analysis.netBenefit < 0 ? 'var(--danger)' : 'var(--success)', background: analysis.netBenefit < 0 ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.08)', padding: '2px 8px', borderRadius: '6px' }}>
                                            Чистая прибыль (ROI): {formatMoney(analysis.netBenefit)}
                                          </span>
                                        </summary>
                                        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px dashed var(--surface-border)', paddingTop: '12px' }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'var(--text-secondary)' }}>Личный оборот ПВЗ (без партнёра):</span>
                                            <span style={{ fontWeight: 600 }}>{formatMoney(analysis.ownTurnover)}</span>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'var(--text-secondary)' }}>Средний тариф WB (с ним / без него):</span>
                                            <span style={{ fontWeight: 600 }}>
                                              {analysis.averageTariff.toFixed(2)}% / {analysis.averageTariffWithout.toFixed(2)}% 
                                              {tariffDiff !== 0 && (
                                                <span style={{ color: 'var(--danger)', marginLeft: '6px', fontWeight: 'bold' }}>
                                                  ({(tariffDiff > 0 ? '+' : '') + tariffDiff.toFixed(2)}%)
                                                </span>
                                              )}
                                            </span>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(0,0,0,0.03)', paddingTop: '6px' }}>
                                            <span style={{ color: 'var(--text-secondary)' }}>Выручка от WB с партнёра:</span>
                                            <span style={{ fontWeight: 600, color: 'var(--success)' }}>+{formatMoney(totalPvzRevenue)}</span>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'var(--text-secondary)' }}>Выплачено партнёру (комиссия):</span>
                                            <span style={{ fontWeight: 600, color: 'var(--warning)' }}>-{formatMoney(totalCommission)}</span>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'var(--text-secondary)' }}>Налог УСН (6%) с партнёра:</span>
                                            <span style={{ fontWeight: 600, color: 'var(--danger)' }}>-{formatMoney(analysis.taxAmount)}</span>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: 'var(--text-secondary)', borderBottom: '1px dashed var(--text-tertiary)', cursor: 'help' }} title="Из-за роста общего оборота ПВЗ ставка тарифа WB снизилась, что привело к снижению выплат на ваш собственный оборот.">Влияние на личный тариф (недополучено):</span>
                                            <span style={{ fontWeight: 600, color: 'var(--danger)' }}>{formatMoney(analysis.tariffLoss)}</span>
                                          </div>

                                          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--surface-border)', paddingTop: '8px', marginTop: '4px', fontWeight: '700' }}>
                                            <span style={{ color: 'var(--text-primary)' }}>Итоговая чистая прибыль (ROI):</span>
                                            <span style={{ color: analysis.netBenefit < 0 ? 'var(--danger)' : 'var(--success)' }}>
                                              {(analysis.netBenefit > 0 ? '+' : '') + formatMoney(analysis.netBenefit)}
                                            </span>
                                          </div>
                                          
                                          <div style={{ borderTop: '1px solid var(--surface-border)', paddingTop: '10px', marginTop: '4px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                            {analysis.netBenefit > 0 ? (
                                              <>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}>
                                                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                                  <polyline points="22 4 12 14.01 9 11.01"/>
                                                </svg>
                                                <span>
                                                  <b>Партнёрство выгодно!</b> Чистый плюс составляет <b>{formatMoney(analysis.netBenefit)}</b> с учётом уплаты налога УСН 6% ({formatMoney(analysis.taxAmount)}) и недополученной выручки от снижения тарифа на ваш собственный оборот ({formatMoney(Math.abs(analysis.tariffLoss))}).
                                                </span>
                                              </>
                                            ) : (
                                              <>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}>
                                                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                                  <line x1="12" y1="9" x2="12" y2="13"/>
                                                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                                                </svg>
                                                <span>
                                                  <b>Партнёрство невыгодно!</b> Вы теряете <b>{formatMoney(Math.abs(analysis.netBenefit))}</b> из-за уплаты налогов и сильного снижения процента тарифа WB на ваш собственный оборот.
                                                </span>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </details>
                                    );
                                  })()}
                                </div>
                              </div>

                              <div style={{ marginTop: 'auto' }}>
                                <button className="btn btn-outline-primary w-100" onClick={() => { setEditPartner(partner); setPartnerModal('add_turnover') }} style={{ height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: '600', fontSize: '14px', borderRadius: '12px' }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                  Добавить запись
                                </button>
                              </div>
                            </div>

                            {/* ПРАВАЯ КОЛОНКА: Детализация */}
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                              {entries.length === 0 && (!useEstimatedRevenue || fDetails.commission <= 0) ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '180px', padding: '20px', background: 'var(--bg-secondary)', border: '1px dashed var(--surface-border)', borderRadius: '16px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
                                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '8px' }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                  <span style={{ fontSize: '13px' }}>Нет записей по обороту. Добавьте первую запись слева.</span>
                                </div>
                              ) : (
                                <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--surface-border)', borderRadius: '16px', padding: '16px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                  <div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary)', marginBottom: '12px' }}>Детализация начислений</div>
                                  
                                  {/* Шапка таблицы */}
                                  <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '8px', fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--surface-border)' }}>
                                    <span style={{ width: '65px', flexShrink: 0 }}>Дата</span>
                                    <span style={{ width: '105px', flexShrink: 0 }}>Оборот</span>
                                    <span style={{ width: '95px', flexShrink: 0 }}>Комиссия</span>
                                    <span style={{ flex: 1, minWidth: 0 }}>Комментарий</span>
                                    <span style={{ width: '56px', flexShrink: 0 }}></span>
                                  </div>

                                  {/* Список записей с красивым скроллом */}
                                  <div style={{ maxHeight: '240px', overflowY: 'auto', paddingRight: '4px', marginTop: '4px' }}>
                                    {entries.map(entry => (
                                      <div key={entry.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.04)', gap: '0' }}>
                                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontWeight: '500', width: '65px', flexShrink: 0 }}>
                                          {new Date(entry.entry_date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                                        </span>
                                        <span style={{ fontSize: '13.5px', fontWeight: '600', whiteSpace: 'nowrap', color: 'var(--text-primary)', width: '105px', flexShrink: 0 }}>
                                          {formatMoney(Number(entry.turnover_amount))}
                                        </span>
                                        <span style={{ fontSize: '13px', color: 'var(--warning)', fontWeight: '700', width: '95px', flexShrink: 0 }}>
                                          {formatMoney(Number(entry.commission_amount))}
                                        </span>
                                        <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, paddingRight: '8px' }} title={entry.comment}>
                                          {entry.comment ? `• ${entry.comment}` : ''}
                                        </span>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', width: '56px', flexShrink: 0 }}>
                                          <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', borderRadius: '4px' }} onClick={() => { setEditPartner({ ...partner, editEntry: entry }); setPartnerModal('edit_turnover') }} title="Редактировать">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                          </button>
                                          <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', borderRadius: '4px' }} onClick={async () => {
                                            if (!confirm('Удалить запись?')) return
                                            try { await api.deletePartnerTurnover(entry.id); toast.success('Удалено'); loadData(true) } catch (e) { toast.error(e.message) }
                                          }} title="Удалить"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                                        </div>
                                      </div>
                                    ))}
                                    {useEstimatedRevenue && fDetails.commission > 0 && (
                                      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.04)', gap: '0', fontStyle: 'italic', background: 'rgba(139, 92, 246, 0.03)' }}>
                                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', fontWeight: '500', width: '65px', flexShrink: 0 }}>
                                          Прогноз
                                        </span>
                                        <span style={{ fontSize: '12.5px', fontWeight: '600', whiteSpace: 'nowrap', color: 'var(--text-secondary)', width: '105px', flexShrink: 0 }}>
                                          {formatMoney(fDetails.turnover)}
                                        </span>
                                        <span style={{ fontSize: '12px', color: '#8B5CF6', fontWeight: '700', width: '95px', flexShrink: 0 }}>
                                          {formatMoney(fDetails.commission)}
                                        </span>
                                        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, paddingRight: '8px' }}>
                                          • {forecastDaysCount} дн. по истории прошлых 30 дней
                                        </span>
                                        <span style={{ width: '56px', flexShrink: 0 }}></span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Модалка: Новый партнёр */}
            <Modal isOpen={partnerModal === 'add_partner'} onClose={() => { setPartnerModal(null); setEditPartner(null) }} title="Новый партнёр">
              <form onSubmit={async (e) => {
                e.preventDefault(); const fd = new FormData(e.target)
                try {
                  await api.addPartner(activePvz.id, { name: fd.get('name'), commission_percent: parseFloat(fd.get('commission_percent')) || 1, description: fd.get('description') || '' })
                  toast.success('Партнёр добавлен!'); setPartnerModal(null); loadData(true)
                } catch (err) { toast.error(err.message) }
              }}>
                <div className="input-group"><label>Название</label><input type="text" name="name" className="input" required placeholder="Магазин iPhone" /></div>
                <div className="input-group"><label>Комиссия</label><div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><input type="number" name="commission_percent" min="0.01" max="100" step="0.01" defaultValue="1" className="input" style={{ width: '90px', textAlign: 'center', fontWeight: 600, fontSize: '16px' }} required /><span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>% от оборота</span></div></div>
                <div className="input-group"><label>Описание</label><input type="text" name="description" className="input" placeholder="Краткое описание" /></div>
                <div className="modal-actions mt-4"><button type="button" className="btn btn-secondary flex-1" onClick={() => setPartnerModal(null)}>Отмена</button><button type="submit" className="btn btn-primary flex-1">Добавить</button></div>
              </form>
            </Modal>

            {/* Модалка: Добавить запись оборота */}
            <Modal isOpen={partnerModal === 'add_turnover'} onClose={() => { setPartnerModal(null); setEditPartner(null) }} title={`${editPartner?.name || ''}: новая запись`} overflowVisible={true}>
              <form onSubmit={async (e) => {
                e.preventDefault(); const fd = new FormData(e.target)
                try {
                  await api.addPartnerTurnover(activePvz.id, { partner_id: editPartner.id, entry_date: fd.get('entry_date'), turnover_amount: parseFloat(fd.get('turnover_amount')) || 0, comment: fd.get('comment') || '' })
                  toast.success('Запись добавлена!'); setPartnerModal(null); setEditPartner(null); loadData(true)
                } catch (err) { toast.error(err.message) }
              }}>
                <div className="input-group"><label>Дата</label><DatePicker name="entry_date" defaultValue={new Date().toISOString().split('T')[0]} required /></div>
                <div className="input-group"><label>Оборот (₽)</label><CurrencyInput name="turnover_amount" required /></div>
                <div className="input-group"><label>Комментарий</label><input type="text" name="comment" className="input" placeholder="Из отчёта за неделю" /></div>
                <div style={{ background: 'var(--bg-secondary)', padding: '10px 14px', borderRadius: '10px', fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--purple)" strokeWidth="2" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  <span>Комиссия: {editPartner?.commission_percent}% от суммы</span>
                </div>
                <div className="modal-actions mt-4"><button type="button" className="btn btn-secondary flex-1" onClick={() => { setPartnerModal(null); setEditPartner(null) }}>Отмена</button><button type="submit" className="btn btn-primary flex-1">Добавить</button></div>
              </form>
            </Modal>

            {/* Модалка: Редактировать запись */}
            <Modal isOpen={partnerModal === 'edit_turnover'} onClose={() => { setPartnerModal(null); setEditPartner(null) }} title="Редактировать запись" overflowVisible={true}>
              {editPartner?.editEntry && (
                <form onSubmit={async (e) => {
                  e.preventDefault(); const fd = new FormData(e.target)
                  try {
                    await api.updatePartnerTurnover(editPartner.editEntry.id, { entry_date: fd.get('entry_date'), turnover_amount: parseFloat(fd.get('turnover_amount')) || 0, comment: fd.get('comment') || '' })
                    toast.success('Обновлено!'); setPartnerModal(null); setEditPartner(null); loadData(true)
                  } catch (err) { toast.error(err.message) }
                }}>
                  <div className="input-group"><label>Дата</label><DatePicker name="entry_date" defaultValue={editPartner.editEntry.entry_date?.split('T')[0]} required /></div>
                  <div className="input-group"><label>Оборот (₽)</label><CurrencyInput name="turnover_amount" defaultValue={editPartner.editEntry.turnover_amount} required /></div>
                  <div className="input-group"><label>Комментарий</label><input type="text" name="comment" className="input" defaultValue={editPartner.editEntry.comment || ''} /></div>
                  <div className="modal-actions mt-4"><button type="button" className="btn btn-secondary flex-1" onClick={() => { setPartnerModal(null); setEditPartner(null) }}>Отмена</button><button type="submit" className="btn btn-primary flex-1">Сохранить</button></div>
                </form>
              )}
            </Modal>

            <Modal isOpen={registryModal} onClose={() => setRegistryModal(false)} title="Реестр операций" isBottomSheet={true} customWidth="850px">
                  <div className="analytics-section-header" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className={`btn ${activeTab === 'incomes' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setActiveTab('incomes')}>Выручка</button>
                    <button className={`btn ${activeTab === 'expenses' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setActiveTab('expenses')}>Расходы</button>
                    <button className={`btn ${activeTab === 'payroll' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setActiveTab('payroll')}>Зарплаты</button>
                    <button className={`btn ${activeTab === 'reserves' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setActiveTab('reserves')} style={{ display: 'none' }}>Резервы</button>
                  </div>
                </div>
                {activeTab !== 'payroll' && (
                  <button className="btn btn-sm btn-primary" onClick={() => {
                    if (activeTab === 'incomes') openModal('action_chooser');
                    else if (activeTab === 'expenses') openModal('expense_chooser');
                    else if (activeTab === 'reserves') openModal('reserve');
                  }}>
                    + Добавить {activeTab === 'incomes' ? 'выручку' : activeTab === 'expenses' ? 'расход' : 'в резерв'}
                  </button>
                )}
              </div>

              <div className="transaction-list">
                {/* FLOATING ACTION BAR FOR BULK DELETE */}
                <div style={{
                  position: 'fixed',
                  bottom: selectedItems.length > 0 ? '40px' : '-100px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--surface)',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.3), 0 0 0 1px var(--surface-border)',
                  padding: '16px 24px',
                  borderRadius: '100px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '24px',
                  transition: 'bottom 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  zIndex: 1000,
                  opacity: selectedItems.length > 0 ? 1 : 0
                }}>
                  <span style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '15px' }}>Выбрано: {selectedItems.length} шт.</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary btn-sm" style={{ borderRadius: '20px' }} onClick={() => setSelectedItems([])}>Снять выбор</button>
                    <button className="btn btn-primary btn-sm" style={{ borderRadius: '20px', background: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => { setItemToDelete({ type: 'bulk', id: selectedItems }); setModalType('confirm_delete'); }}>
                      Удалить
                    </button>
                  </div>
                </div>

                {activeTab === 'incomes' && (
                  displayedIncomes.length === 0 ? <p className="text-secondary" style={{ padding: '20px', textAlign: 'center' }}>Нет доходов за этот период</p> :
                  <div className="custom-table-wrapper">
                    <table className="custom-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}><input type="checkbox" checked={selectedItems.length === displayedIncomes.length && displayedIncomes.length > 0} onChange={() => toggleSelectAll(displayedIncomes)} /></th>
                          <th>Дата / Период</th><th>Источник</th><th>Комментарий</th><th style={{ textAlign: 'right' }}>Сумма</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedIncomes.map(inc => (
                          <tr key={inc.id} className={selectedItems.includes(inc.id) ? 'selected-row' : ''}>
                            <td><input type="checkbox" checked={selectedItems.includes(inc.id)} onChange={() => toggleSelection(inc.id)} /></td>
                            <td>{inc.period_start && inc.period_end ? `${new Date(inc.period_start).toLocaleDateString('ru-RU', {day:'2-digit', month:'2-digit'})} - ${new Date(inc.period_end).toLocaleDateString('ru-RU', {day:'2-digit', month:'2-digit'})}` : new Date(inc.date).toLocaleDateString('ru-RU')}</td>
                            <td>{inc.source || 'Не указан'}</td>
                            <td>{inc.comment || '-'}</td>
                            <td style={{ textAlign: 'right', fontWeight: '600' }} className="text-green">+{formatMoney(Number(inc.amount))}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap', width: '1%' }}>
                              <button className="btn-icon-edit" onClick={() => openModal(inc.source === 'Wildberries' ? 'income' : 'income_other', '', inc)} title="Редактировать"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                              <button className="btn-icon-delete" onClick={() => handleDelete('income', inc.id)} title="Удалить"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>
                            </td>
                          </tr>
                        ))}
                        {useEstimatedRevenue && forecastDaysCount > 0 && (
                          <tr style={{ background: 'rgba(16, 185, 129, 0.05)', fontStyle: 'italic' }}>
                            <td></td>
                            <td>Будущие дни (Прогноз)</td>
                            <td>Wildberries (Прогноз)</td>
                            <td>Прогноз на {forecastDaysCount} дней по ставке {currentForecastTariff.toFixed(2)}%</td>
                            <td style={{ textAlign: 'right', fontWeight: '600' }} className="text-green">+{formatMoney(estimatedWbIncome - actualWbIncome)}</td>
                            <td></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                {activeTab === 'expenses' && (
                  <>
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--text-secondary)' }}>Постоянные расходы (Планы авто-начислений)</h4>
                    {activePlans.length === 0 ? <p className="text-secondary" style={{ padding: '20px', textAlign: 'center', background: 'var(--surface-solid)', borderRadius: '12px' }}>Нет активных постоянных расходов</p> :
                    <div className="custom-table-wrapper">
                      <table className="custom-table">
                        <thead>
                          <tr>
                            <th>Категория</th>
                            <th style={{ textAlign: 'right' }}>Сумма в месяц</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {activePlans.map(plan => (
                              <tr key={plan.id}>
                                <td>{plan.category || plan.title}</td>
                                <td style={{ textAlign: 'right', fontWeight: '600' }} className="text-red">-{formatMoney(Number(plan.amount || plan.monthly_amount))}</td>
                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap', width: '1%' }}>
                                  <button className="btn-icon-edit" onClick={() => openModal('plan', plan.id, plan)} title="Редактировать"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                                  <button className="btn-icon-delete" onClick={() => handleDelete('plan', plan.id)} title="Удалить"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    }
                  </div>

                  <h4 style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--text-secondary)' }}>Переменные расходы (Разовые)</h4>
                  {expenses.length === 0 ? <p className="text-secondary" style={{ padding: '20px', textAlign: 'center' }}>Нет расходов за этот период</p> :
                  <div className="custom-table-wrapper">
                    <table className="custom-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}><input type="checkbox" checked={selectedItems.length === expenses.length && expenses.length > 0} onChange={() => toggleSelectAll(expenses)} /></th>
                          <th>Дата</th><th>Категория</th><th>Комментарий</th><th style={{ textAlign: 'right' }}>Сумма</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {expenses.map(exp => (
                          <tr key={exp.id} className={selectedItems.includes(exp.id) ? 'selected-row' : ''}>
                            <td><input type="checkbox" checked={selectedItems.includes(exp.id)} onChange={() => toggleSelection(exp.id)} /></td>
                            <td>{new Date(exp.date).toLocaleDateString('ru-RU')}</td>
                            <td>{exp.category || (exp.fixed_plan_id ? 'Постоянный' : 'Переменный')}</td>
                            <td>{exp.comment || '-'}</td>
                            <td style={{ textAlign: 'right', fontWeight: '600' }} className="text-red">-{formatMoney(Number(exp.amount))}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap', width: '1%' }}>
                              <button className="btn-icon-edit" onClick={() => openModal(exp.fixed_plan_id ? 'expense_fixed' : 'expense_var', exp.fixed_plan_id, exp)} title="Редактировать"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                              <button className="btn-icon-delete" onClick={() => handleDelete('expense', exp.id)} title="Удалить"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  }
                  </>
                )}
                {activeTab === 'reserves' && (
                  reserves.length === 0 ? <p className="text-secondary" style={{ padding: '20px', textAlign: 'center' }}>Нет резервов за этот период</p> :
                  <div className="custom-table-wrapper">
                    <table className="custom-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}><input type="checkbox" checked={selectedItems.length === reserves.length && reserves.length > 0} onChange={() => toggleSelectAll(reserves)} /></th>
                          <th>Дата</th><th>Комментарий</th><th style={{ textAlign: 'right' }}>Сумма</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {reserves.map(rsv => (
                          <tr key={rsv.id} className={selectedItems.includes(rsv.id) ? 'selected-row' : ''}>
                            <td><input type="checkbox" checked={selectedItems.includes(rsv.id)} onChange={() => toggleSelection(rsv.id)} /></td>
                            <td>{new Date(rsv.date || rsv.created_at).toLocaleDateString('ru-RU')}</td>
                            <td>{rsv.comment || '-'}</td>
                            <td style={{ textAlign: 'right', fontWeight: '600' }} style={{ color: 'var(--purple)' }}>{formatMoney(Number(rsv.amount))}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap', width: '1%' }}><button className="btn-icon-delete" onClick={() => handleDelete('reserve', rsv.id)}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {activeTab === 'payroll' && (
                  salaryList.length === 0 ? <p className="text-secondary" style={{ padding: '20px', textAlign: 'center' }}>Нет данных по зарплатам за этот период</p> :
                  <div className="custom-table-wrapper">
                    <table className="custom-table">
                      <thead>
                        <tr>
                          <th>Сотрудник</th>
                          <th style={{ textAlign: 'center' }}>Выходы</th>
                          <th style={{ textAlign: 'right' }}>Доплаты/Штрафы</th>
                          <th style={{ textAlign: 'right' }}>К выплате</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salaryList.map((sal, idx) => {
                          const forecastAmt = useEstimatedRevenue && forecastDaysCount > 0 ? (employeePastAvgPayroll[sal.employeeId] || 0) * forecastDaysCount : 0
                          const factAmt = Math.max(sal.total || 0, 0)
                          const totalAmt = factAmt + forecastAmt
                          return (
                            <tr key={idx}>
                              <td>
                                <div style={{ fontWeight: '600' }}>{sal.employeeName}</div>
                                {sal.shiftsCount > 0 && <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Ставка: {formatMoney(sal.baseRate)}</div>}
                                {forecastAmt > 0 && (
                                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                    Факт: {formatMoney(factAmt)} + Прогноз: {formatMoney(forecastAmt)}
                                  </div>
                                )}
                              </td>
                              <td style={{ textAlign: 'center' }}>{sal.shiftsCount || 0}</td>
                              <td style={{ textAlign: 'right' }}>
                                <span className="text-green">+{formatMoney(sal.totalBonuses || 0)}</span> / <span className="text-red">-{formatMoney((sal.totalFines || 0) + (sal.totalWbDeductions || 0))}</span>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: '600', color: 'var(--orange)' }}>{formatMoney(totalAmt)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Modal>

          </motion.div>
        )}
      </div>

      {/* МОДАЛЬНЫЕ ОКНА */}
      <Modal isOpen={modalType === 'action_chooser'} onClose={() => setModalType(null)} title="Что добавить?">
        <div className="action-chooser-grid">
              <div className="action-chooser-item" onClick={() => openModal('income')}>
                <span className="action-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></span>
                <div className="action-text">
                  <strong>Поступление WB</strong>
                  <span>Деньги, пришедшие от Wildberries</span>
                </div>
              </div>
              <div className="action-chooser-item" onClick={() => openModal('income_other')}>
                <span className="action-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg></span>
                <div className="action-text">
                  <strong>Другое поступление</strong>
                  <span>Любой другой доход ПВЗ</span>
                </div>
              </div>
              <div className="action-chooser-item" onClick={() => openModal('expense_var')}>
                <span className="action-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg></span>
                <div className="action-text">
                  <strong>Переменный расход</strong>
                  <span>Разовые: ремонт, расходники и т.д.</span>
                </div>
              </div>
              <div className="action-chooser-item" onClick={() => openModal('plan')}>
                <span className="action-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01"/></svg></span>
                <div className="action-text">
                  <strong>План постоянного расхода</strong>
                  <span>Аренда, интернет (авто-начисление)</span>
                </div>
              </div>
              <div className="action-chooser-item" onClick={() => openModal('reserve')}>
                <span className="action-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>
                <div className="action-text">
                  <strong>Отложить в резерв</strong>
                  <span>Отложили деньги на будущий платеж</span>
                </div>
              </div>
              <div className="action-chooser-item" onClick={() => { setModalType(null); setShowImportWizard(true); }}>
                <span className="action-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg></span>
                <div className="action-text">
                  <strong>Импорт из Excel</strong>
                  <span>Массовая загрузка доходов WB</span>
                </div>
              </div>
            </div>
      </Modal>

      <Modal isOpen={modalType === 'expense_chooser'} onClose={() => setModalType(null)} title="Какой расход добавить?">
        <div className="action-chooser-grid">
          <div className="action-chooser-item" onClick={() => openModal('expense_var')}>
            <span className="action-icon"></span>
            <div className="action-text">
              <strong>Переменный расход</strong>
              <span>Разовые траты: хозтовары, ремонт и т.д.</span>
            </div>
          </div>
          <div className="action-chooser-item" onClick={() => openModal('plan')}>
            <span className="action-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01"/></svg></span>
            <div className="action-text">
              <strong>Постоянный расход</strong>
              <span>Регулярные траты: аренда, налоги (начисляются автоматически)</span>
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={modalType === 'income'} onClose={() => { setModalType(null); setEditItem(null); }} title={editItem ? "Редактировать поступление" : "Поступление от Wildberries"} overflowVisible={true}>
        <form onSubmit={async (e) => {
              e.preventDefault(); const fd = new FormData(e.target);
              try {
                const data = {
                  date: fd.get('date'), period_start: fd.get('period_start'), period_end: fd.get('period_end'),
                  amount: fd.get('amount') || 0, compensation: fd.get('compensation') || 0,
                  bonus: fd.get('bonus') || 0, penalty: fd.get('penalty') || 0,
                  comment: fd.get('comment'), source: 'Wildberries'
                };
                if (editItem) await api.updateWbIncome(editItem.id, data);
                else await api.addWbIncome(data, activePvz.id);
                toast.success('Поступление WB сохранено!'); setModalType(null); setEditItem(null); loadData(true);
              } catch (err) { toast.error(err.message) }
            }}>
              <div className="input-group" style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label>Период с</label>
                  <DatePicker name="period_start" className="input" defaultValue={editItem?.period_start ? new Date(editItem.period_start).toISOString().split('T')[0] : modalDate} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label>Период по</label>
                  <DatePicker name="period_end" className="input" defaultValue={editItem?.period_end ? new Date(editItem.period_end).toISOString().split('T')[0] : modalDate} required />
                </div>
              </div>
              <DatePickerField label="Дата зачисления" name="date" value={modalDate} onChange={setModalDate} required />
              <div className="input-group mt-3"><label>Сумма к выплате (₽)</label><CurrencyInput name="amount" defaultValue={editItem?.amount} required /></div>
              <div className="input-group"><label>Компенсации (₽)</label><CurrencyInput name="compensation" defaultValue={editItem?.compensation} /></div>
              <div className="input-group"><label>Бонусы (₽)</label><CurrencyInput name="bonus" defaultValue={editItem?.bonus} /></div>
              <div className="input-group"><label>Удержания/Штрафы WB (₽)</label><CurrencyInput name="penalty" defaultValue={editItem?.penalty} /></div>
              <div className="input-group"><label>Комментарий</label><input type="text" name="comment" defaultValue={editItem?.comment} className="input" /></div>
              <div className="modal-actions mt-4"><button type="button" className="btn btn-secondary flex-1" onClick={() => { setModalType(null); setEditItem(null); }}>Отмена</button><button type="submit" className="btn btn-primary flex-1">Сохранить</button></div>
            </form>
      </Modal>

      <Modal isOpen={modalType === 'income_other'} onClose={() => { setModalType(null); setEditItem(null); }} title={editItem ? "Редактировать поступление" : "Другое поступление"} overflowVisible={true}>
        <form onSubmit={async (e) => {
              e.preventDefault(); const fd = new FormData(e.target);
              try {
                const data = {
                  date: fd.get('date'), period_start: fd.get('period_start'), period_end: fd.get('period_end'),
                  amount: fd.get('amount') || 0,
                  comment: fd.get('comment'), source: fd.get('source')
                };
                if (editItem) await api.updateWbIncome(editItem.id, data);
                else await api.addWbIncome(data, activePvz.id);
                toast.success('Поступление сохранено!'); setModalType(null); setEditItem(null); loadData(true);
              } catch (err) { toast.error(err.message) }
            }}>
              <div className="input-group">
                <CustomSelect
                  label="Источник"
                  value={modalSource}
                  onChange={setModalSource}
                  options={[
                    { value: 'Выдача посылок', label: 'Выдача посылок (Drop-off)' },
                    { value: 'Субаренда', label: 'Субаренда' },
                    { value: 'Прочее', label: 'Прочее' },
                  ]}
                />
                <input type="hidden" name="source" value={modalSource} />
              </div>
              <div className="input-group" style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label>Период с</label>
                  <DatePicker name="period_start" className="input" defaultValue={editItem?.period_start ? new Date(editItem.period_start).toISOString().split('T')[0] : modalDate} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label>Период по</label>
                  <DatePicker name="period_end" className="input" defaultValue={editItem?.period_end ? new Date(editItem.period_end).toISOString().split('T')[0] : modalDate} required />
                </div>
              </div>
              <DatePickerField label="Дата зачисления" name="date" value={modalDate} onChange={setModalDate} required />
              <div className="input-group mt-3"><label>Сумма (₽)</label><CurrencyInput name="amount" defaultValue={editItem?.amount} required /></div>
              <div className="input-group"><label>Комментарий</label><input type="text" name="comment" defaultValue={editItem?.comment} className="input" /></div>
              <div className="modal-actions mt-4"><button type="button" className="btn btn-secondary flex-1" onClick={() => { setModalType(null); setEditItem(null); }}>Отмена</button><button type="submit" className="btn btn-primary flex-1">Сохранить</button></div>
            </form>
      </Modal>

      <Modal isOpen={modalType === 'expense_var'} onClose={() => { setModalType(null); setEditItem(null); }} title={editItem ? "Редактировать расход" : "Переменный расход"} overflowVisible={true}>
        <form onSubmit={async (e) => {
              e.preventDefault(); const fd = new FormData(e.target);
              try {
                const data = {
                  date: fd.get('date'), category: fd.get('category'), amount: fd.get('amount') || 0,
                  comment: fd.get('comment'), type: 'variable', is_paid: true
                };
                if (editItem) await api.updateExpense(editItem.id, data);
                else await api.addExpense(data, activePvz.id);
                toast.success('Расход сохранен!'); setModalType(null); setEditItem(null); loadData(true);
              } catch (err) { toast.error(err.message) }
            }}>
              <DatePickerField label="Дата оплаты" name="date" value={modalDate} onChange={setModalDate} required />
              <div className="input-group mt-3">
                <CustomSelect
                  label="Категория"
                  value={modalCategory}
                  onChange={setModalCategory}
                  options={EXPENSE_CATEGORIES.map(c => ({
                    value: c.label,
                    label: c.label,
                  }))}
                />
                <input type="hidden" name="category" value={modalCategory} />
              </div>
              <div className="input-group"><label>Сумма (₽)</label><CurrencyInput name="amount" defaultValue={editItem?.amount} required /></div>
              <div className="input-group"><label>Комментарий</label><input type="text" name="comment" defaultValue={editItem?.comment} className="input" /></div>
              <div className="modal-actions mt-4"><button type="button" className="btn btn-secondary flex-1" onClick={() => { setModalType(null); setEditItem(null); }}>Отмена</button><button type="submit" className="btn btn-primary flex-1">Сохранить расход</button></div>
            </form>
      </Modal>

      <Modal isOpen={modalType === 'plan'} onClose={() => { setModalType(null); setEditItem(null); }} title={editItem ? "Редактировать план" : "План постоянного расхода"}>
        <form onSubmit={async (e) => {
              e.preventDefault(); const fd = new FormData(e.target);
              try {
                const data = {
                  name: fd.get('category'), title: fd.get('category'), category: fd.get('category'),
                  amount: fd.get('amount') || 0, monthly_amount: fd.get('amount') || 0,
                  due_day: parseInt(fd.get('due_day')) || 1,
                  accounting_method: 'fact_only', is_active: true, is_auto_reserve: fd.get('is_auto_reserve') === 'on'
                };
                if (editItem) {
                  await api.updateFixedExpenseTemplate(editItem.id, data);
                  toast.success('План обновлен!');
                } else {
                  await api.addFixedExpenseTemplate(activePvz.id, data);
                  toast.success('План создан!');
                }
                setModalType(null); setEditItem(null); loadData(true);
              } catch (err) { toast.error(err.message) }
            }}>
              <div className="input-group"><label>Название/Категория</label><input type="text" name="category" defaultValue={editItem?.category || editItem?.title} className="input" required placeholder="Например: Аренда" /></div>
              <div className="input-group"><label>Сумма в месяц (₽)</label><CurrencyInput name="amount" defaultValue={editItem?.amount || editItem?.monthly_amount} required /></div>
              <div className="input-group">
                <label>Оплата до какого числа месяца</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type="number" name="due_day" min="1" max="31" defaultValue={editItem?.due_day || 1} className="input" style={{ width: '80px', textAlign: 'center', fontWeight: 600, fontSize: '16px' }} required />
                  <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>числа каждого месяца</span>
                </div>
                <div className="text-secondary mt-1" style={{ fontSize: '12px' }}>Система будет копить резерв так, чтобы к этому числу вся сумма была готова</div>
              </div>
              <div className="checkbox-group mt-3" style={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center', justifyContent: 'flex-start' }}>
                <input type="checkbox" id="is_auto_reserve" name="is_auto_reserve" defaultChecked={editItem ? editItem.is_auto_reserve : true} style={{ width: '20px', height: '20px', margin: 0, cursor: 'pointer' }} />
                <label htmlFor="is_auto_reserve" style={{ margin: 0, cursor: 'pointer', fontSize: '14px', color: 'var(--text-primary)' }}>Умный авто-сбор из доходов</label>
              </div>
              <div className="text-secondary mt-2" style={{ fontSize: '13px', lineHeight: 1.4, background: 'var(--bg-secondary)', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--surface-border)', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--purple)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                <span>Система рассчитает, какую долю от ежедневного дохода нужно откладывать, чтобы к дате оплаты собрать всю сумму. Чем ближе дата — тем больший процент дохода откладывается.</span>
              </div>
              <div className="modal-actions mt-4"><button type="button" className="btn btn-secondary flex-1" onClick={() => { setModalType(null); setEditItem(null); }}>Отмена</button><button type="submit" className="btn btn-primary flex-1">Сохранить план</button></div>
            </form>
      </Modal>

      <Modal isOpen={modalType === 'expense_fixed' || modalType === 'reserve'} onClose={() => { setModalType(null); setEditItem(null); }} title={editItem ? "Редактировать запись" : (modalType === 'reserve' ? "Отложить в резерв" : "Оплата постоянного расхода")} overflowVisible={true}>
        <form onSubmit={async (e) => {
              e.preventDefault(); const fd = new FormData(e.target);
              const templateId = fd.get('template_id');
              const amount = fd.get('amount');
              const plan = fixedPlans.find(p => p.id === templateId);
              try {
                if (modalType === 'reserve') {
                  const data = { template_id: templateId, fixed_plan_id: templateId, amount: amount, comment: fd.get('comment'), date: fd.get('date') };
                  if (editItem) {
                    await api.deleteReserve(editItem.id);
                    await api.addReserve(activePvz.id, data);
                  } else await api.addReserve(activePvz.id, data);
                  toast.success('Резерв сохранен!');
                } else {
                  const paymentDate = fd.get('period_month') ? `${fd.get('period_month')}-01` : fd.get('date');
                  const data = {
                    date: paymentDate, category: plan ? (plan.category || plan.title) : 'Постоянный расход', amount: amount,
                    comment: fd.get('comment'), type: 'fixed_payment', is_paid: true, fixed_plan_id: templateId
                  };
                  if (editItem) await api.updateExpense(editItem.id, data);
                  else await api.addExpense(data, activePvz.id);
                  toast.success('Оплата сохранена!');
                }
                setModalType(null); setEditItem(null); loadData(true);
              } catch (err) { toast.error(err.message) }
            }}>
              {fixedPlans.length === 0 ? (
                <div className="empty-state">Сначала нужно создать план постоянного расхода.</div>
              ) : (
                <>
                  <div className="input-group">
                    <CustomSelect
                      label="Выберите план расходов"
                      value={selectedPlanId}
                      onChange={setSelectedPlanId}
                      placeholder="-- Выберите --"
                      options={fixedPlans.map(p => ({
                        value: p.id,
                        label: `${p.category || p.title} (${p.amount || p.monthly_amount} ₽)`,
                      }))}
                    />
                    <input type="hidden" name="template_id" value={selectedPlanId} />
                  </div>
                  {modalType === 'expense_fixed' ? (
                    <div className="input-group">
                      <label>Месяц начисления (для распределения)</label>
                      <input type="month" name="period_month" className="input" defaultValue={editItem?.date ? editItem.date.substring(0, 7) : modalDate.substring(0, 7)} required />
                      <div className="text-secondary mt-1" style={{ fontSize: '11px', lineHeight: 1.2 }}>Сумма будет грамотно распределена (пропорционально дням) по выбранному месяцу в аналитике.</div>
                    </div>
                  ) : (
                    <DatePickerField label="Дата" name="date" value={modalDate} onChange={setModalDate} required />
                  )}
                  <div className="input-group mt-3"><label>Сумма (₽)</label><CurrencyInput name="amount" defaultValue={editItem?.amount} required /></div>
                  <div className="input-group"><label>Комментарий</label><input type="text" name="comment" defaultValue={editItem?.comment} className="input" /></div>
                  <div className="modal-actions mt-4"><button type="button" className="btn btn-secondary flex-1" onClick={() => { setModalType(null); setEditItem(null); }}>Отмена</button><button type="submit" className="btn btn-primary flex-1">Сохранить</button></div>
                </>
              )}
        </form>
      </Modal>

      <Modal isOpen={modalType === 'confirm_delete'} onClose={() => { setModalType(null); setItemToDelete(null); }} title="Удаление записи">
        <div style={{ padding: '10px 0 24px 0', color: 'var(--text-secondary)' }}>
          Вы уверены, что хотите удалить {itemToDelete?.type === 'bulk' ? 'выбранные записи' : 'эту запись'}? Это действие нельзя отменить.
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => { setModalType(null); setItemToDelete(null); }}>
            Отмена
          </button>
          <button className="btn btn-primary" style={{ flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={handleConfirmDelete}>
            Удалить
          </button>
        </div>
      </Modal>
      <Modal isOpen={!!dayDetailsData} onClose={() => setDayDetailsData(null)} title={`Детализация за ${dayDetailsData?.displayDate || ''}`}>
        {dayDetailsData && (
          <div className="day-details-content">
            <div className="details-summary" style={{ display: 'flex', gap: '16px', marginBottom: '20px', background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Доходы</div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--success)' }}>{formatMoney(dayDetailsData.income)}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Расходы</div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--danger)' }}>-{formatMoney(dayDetailsData.expense)}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Прибыль</div>
                <div style={{ fontSize: '18px', fontWeight: '600', color: dayDetailsData.realProfit < 0 ? 'var(--danger)' : 'var(--wb-primary)' }}>{formatMoney(dayDetailsData.realProfit)}</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 250px' }}>
                <h4 style={{ margin: '0 0 12px 0', paddingBottom: '8px', borderBottom: '1px solid var(--border-color)', color: 'var(--success)' }}>Доходы</h4>
                {dayDetailsData.details.incomes.length === 0 ? (
                  <div className="text-secondary text-sm">В этот день не было доходов</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {dayDetailsData.details.incomes.map((i, idx) => (
                      <div key={idx} style={{ 
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        fontSize: '14px', padding: '10px 14px', background: 'var(--bg-secondary)', 
                        borderRadius: '8px', border: '1px solid var(--border-color)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--success)'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
                          <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: 'var(--success)' }}></span>
                          {i.name}
                        </span>
                        <span style={{ fontWeight: 600, color: 'var(--success)' }}>+{formatMoney(i.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ flex: '1 1 250px' }}>
                <h4 style={{ margin: '0 0 12px 0', paddingBottom: '8px', borderBottom: '1px solid var(--border-color)', color: 'var(--danger)' }}>Расходы</h4>
                {dayDetailsData.details.expenses.length === 0 ? (
                  <div className="text-secondary text-sm">В этот день не было расходов</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {dayDetailsData.details.expenses.map((e, idx) => (
                      <div key={idx} style={{ 
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        fontSize: '14px', padding: '10px 14px', background: 'var(--bg-secondary)', 
                        borderRadius: '8px', border: '1px solid var(--border-color)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--danger)'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
                          <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: 'var(--danger)' }}></span>
                          {e.name}
                        </span>
                        <span style={{ fontWeight: 600, color: 'var(--danger)' }}>-{formatMoney(e.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6">
              <button className="btn btn-secondary w-full" onClick={() => setDayDetailsData(null)}>Закрыть</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal 
        isOpen={showExpensesDetails} 
        onClose={() => { setShowExpensesDetails(false); setExpenseDetailsSubScreen(null); }} 
        title={
          expenseDetailsSubScreen === 'payroll' ? "Детализация: Зарплаты (ФОТ)" :
          expenseDetailsSubScreen === 'fixed' ? "Детализация: Постоянные расходы" :
          expenseDetailsSubScreen === 'variable' ? "Детализация: Переменные расходы" :
          expenseDetailsSubScreen === 'taxes' ? "Детализация: Налог УСН (6%)" :
          expenseDetailsSubScreen === 'partners' ? "Детализация: Расходы на партнёров" :
          "Детализация расходов"
        } 
        customWidth="500px"
      >
        <div style={{ padding: '8px 0' }}>
          {expenseDetailsSubScreen === null ? (
            <>
              <div style={{ textAlign: 'center', marginBottom: '24px', background: 'var(--bg-secondary)', padding: '16px 20px', borderRadius: '16px', border: '1px solid var(--surface-border)' }}>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Всего расходов за период</div>
                <div style={{ fontSize: '28px', fontWeight: '800', color: 'var(--danger)' }}>{formatMoney(totalExpensesAll)}</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {(() => {
                  const getPctOfIncome = (val) => {
                    if (!totalIncome || totalIncome <= 0) return '0.0';
                    return ((val / totalIncome) * 100).toFixed(1);
                  };
                  return (
                    <>
                      {payrollTotal > 0 && (
                        <div 
                          className="pl-row" 
                          onClick={() => handleExpenseGroupClick('payroll')}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#F59E0B' }}></span>
                            <strong style={{ fontSize: '15px' }}>ФОТ (Зарплаты)</strong>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>{formatMoney(payrollTotal)}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{getPctOfIncome(payrollTotal)}% от выручки</div>
                          </div>
                        </div>
                      )}

                      {fixedPaidExpenses > 0 && (
                        <div 
                          className="pl-row" 
                          onClick={() => handleExpenseGroupClick('fixed')}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#10B981' }}></span>
                            <strong style={{ fontSize: '15px' }}>Постоянные расходы</strong>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>{formatMoney(fixedPaidExpenses)}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{getPctOfIncome(fixedPaidExpenses)}% от выручки</div>
                          </div>
                        </div>
                      )}

                      {variableExpenses > 0 && (
                        <div 
                          className="pl-row" 
                          onClick={() => handleExpenseGroupClick('variable')}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#EF4444' }}></span>
                            <strong style={{ fontSize: '15px' }}>Переменные расходы</strong>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>{formatMoney(variableExpenses)}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{getPctOfIncome(variableExpenses)}% от выручки</div>
                          </div>
                        </div>
                      )}

                      {taxAmount > 0 && (
                        <div 
                          className="pl-row" 
                          onClick={() => handleExpenseGroupClick('taxes')}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#3B82F6' }}></span>
                            <strong style={{ fontSize: '15px' }}>Налог УСН (6%)</strong>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>{formatMoney(taxAmount)}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{getPctOfIncome(taxAmount)}% от выручки</div>
                          </div>
                        </div>
                      )}

                      {totalPartnerCommission > 0 && (
                        <div 
                          className="pl-row" 
                          onClick={() => handleExpenseGroupClick('partners')}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#8B5CF6' }}></span>
                            <strong style={{ fontSize: '15px' }}>Расходы на партнёров</strong>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>{formatMoney(totalPartnerCommission)}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{getPctOfIncome(totalPartnerCommission)}% от выручки</div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="mt-6" style={{ display: 'flex', gap: '12px' }}>
                <button className="btn btn-secondary flex-1" onClick={() => setShowExpensesDetails(false)}>Закрыть</button>
              </div>
            </>
          ) : (
            <>
              {/* Верхняя панель навигации (кнопка Назад) */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid var(--surface-border)', paddingBottom: '12px' }}>
                <button 
                  className="btn btn-ghost btn-sm" 
                  onClick={() => { setExpenseDetailsSubScreen(null); setOpenEmployeeId(null); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 600, padding: '4px 12px', borderRadius: '8px', color: 'var(--text-primary)' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                  Назад к группам
                </button>
              </div>

              <div style={{ maxHeight: '380px', overflowY: 'auto', paddingRight: '4px' }}>
                {expenseDetailsSubScreen === 'payroll' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {(() => {
                      const filteredSalaries = salaryList.filter(sal => {
                        const forecastAmt = useEstimatedRevenue && forecastDaysCount > 0 ? (employeePastAvgPayroll[sal.employeeId] || 0) * forecastDaysCount : 0;
                        const factAmt = Math.max(sal.total || 0, 0);
                        const totalAmt = factAmt + forecastAmt;
                        return totalAmt > 0;
                      });

                      if (filteredSalaries.length === 0) {
                        return (
                          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            Нет начисленных зарплат за выбранный период
                          </div>
                        );
                      }

                      return filteredSalaries.map((sal, idx) => {
                        const forecastAmt = useEstimatedRevenue && forecastDaysCount > 0 ? (employeePastAvgPayroll[sal.employeeId] || 0) * forecastDaysCount : 0;
                        const factAmt = Math.max(sal.total || 0, 0);
                        const totalAmt = factAmt + forecastAmt;
                        const isOpen = openEmployeeId === sal.employeeId;
                        
                        return (
                          <div key={idx} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--surface-border)', borderRadius: '10px', padding: '12px 14px', overflow: 'hidden', transition: 'all 0.3s ease' }}>
                            <div 
                              onClick={() => setOpenEmployeeId(isOpen ? null : sal.employeeId)}
                              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                            >
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{sal.employeeName}</span>
                                {forecastAmt > 0 && (
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                    Факт: {formatMoney(factAmt)} + Прогноз: {formatMoney(forecastAmt)}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontWeight: '700', color: 'var(--orange)' }}>{formatMoney(totalAmt)}</span>
                                <svg 
                                  width="14" 
                                  height="14" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2.5" 
                                  style={{ 
                                    color: 'var(--text-secondary)', 
                                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' 
                                  }}
                                >
                                  <polyline points="6 9 12 15 18 9"/>
                                </svg>
                              </div>
                            </div>
                            
                            <div 
                              style={{ 
                                display: 'grid', 
                                gridTemplateRows: isOpen ? '1fr' : '0fr', 
                                transition: 'grid-template-rows 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin-top 0.3s ease, padding-top 0.3s ease',
                                borderTop: isOpen ? '1px dashed var(--surface-border)' : '1px dashed transparent',
                                marginTop: isOpen ? '12px' : '0px',
                                paddingTop: isOpen ? '10px' : '0px'
                              }}
                            >
                              <div style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                {(sal.daysWorked > 0 || sal.totalHours > 0) && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Смены ({sal.daysWorked || 0} дн. / {sal.totalHours || 0} ч.):</span>
                                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatMoney(sal.basePay || 0)}</span>
                                  </div>
                                )}
                                {(sal.acceptBonus || 0) > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Приёмка коробок:</span>
                                    <span style={{ fontWeight: 600, color: 'var(--success)' }}>+{formatMoney(sal.acceptBonus)}</span>
                                  </div>
                                )}
                                {(sal.issuedBonus || 0) > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Выдача клиентам:</span>
                                    <span style={{ fontWeight: 600, color: 'var(--success)' }}>+{formatMoney(sal.issuedBonus)}</span>
                                  </div>
                                )}
                                {(sal.returnBonus || 0) > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Обработка возвратов:</span>
                                    <span style={{ fontWeight: 600, color: 'var(--success)' }}>+{formatMoney(sal.returnBonus)}</span>
                                  </div>
                                )}
                                {(sal.customWorkBonus || 0) > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Доп. услуги:</span>
                                    <span style={{ fontWeight: 600, color: 'var(--success)' }}>+{formatMoney(sal.customWorkBonus)}</span>
                                  </div>
                                )}
                                {(sal.totalBonuses || 0) > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Ручные премии:</span>
                                    <span style={{ fontWeight: 600, color: 'var(--success)' }}>+{formatMoney(sal.totalBonuses)}</span>
                                  </div>
                                )}
                                {(sal.totalFines || 0) > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Штрафы:</span>
                                    <span style={{ fontWeight: 600, color: 'var(--danger)' }}>-{formatMoney(sal.totalFines)}</span>
                                  </div>
                                )}
                                {(sal.helperDeduction || 0) > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Вычет за помощника:</span>
                                    <span style={{ fontWeight: 600, color: 'var(--danger)' }}>-{formatMoney(sal.helperDeduction)}</span>
                                  </div>
                                )}
                                {(sal.totalWbDeductions || 0) > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Удержания WB:</span>
                                    <span style={{ fontWeight: 600, color: 'var(--danger)' }}>-{formatMoney(sal.totalWbDeductions)}</span>
                                  </div>
                                )}
                                {forecastAmt > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dotted var(--surface-border)', paddingTop: '6px', marginTop: '2px' }}>
                                    <span>Прогноз ФОТ ({forecastDaysCount} дн.):</span>
                                    <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>+{formatMoney(forecastAmt)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}

                {expenseDetailsSubScreen === 'fixed' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {activePlans.map(p => {
                      const amount = Number(p.amount || p.monthly_amount || 0);
                      const rStart = new Date(filterStartDate + 'T00:00:00');
                      const rEnd = new Date(filterEndDate + 'T23:59:59');
                      const cutoffDateStr = useEstimatedRevenue ? filterEndDate : (maxIncomeDateStr || filterStartDate);
                      const cutoffEnd = new Date(Math.min(rEnd.getTime(), new Date(cutoffDateStr + 'T23:59:59').getTime()));
                      
                      let prorated = 0;
                      if (rStart <= cutoffEnd) {
                        let current = new Date(rStart.getFullYear(), rStart.getMonth(), 1);
                        while (current <= cutoffEnd) {
                          const mYear = current.getFullYear(), mMonth = current.getMonth();
                          const dim = new Date(mYear, mMonth + 1, 0).getDate();
                          const mStart = new Date(mYear, mMonth, 1);
                          const mEnd = new Date(mYear, mMonth + 1, 0, 23, 59, 59);
                          const overlapStart = Math.max(mStart.getTime(), rStart.getTime());
                          const overlapEnd = Math.min(mEnd.getTime(), cutoffEnd.getTime());
                          if (overlapStart <= overlapEnd) {
                            prorated += (amount / dim) * Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24));
                          }
                          current.setMonth(current.getMonth() + 1);
                        }
                      }
                      return (
                        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--surface-border)', gap: '10px' }}>
                          <span style={{ fontWeight: '600' }}>{p.category || p.title}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: '700', color: 'var(--success)' }}>{formatMoney(prorated)}</span>
                            <button className="btn-icon-edit" style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }} onClick={() => { setShowExpensesDetails(false); openModal('plan', p.id, p); }} title="Редактировать">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                            <button className="btn-icon-delete" style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }} onClick={() => { setShowExpensesDetails(false); handleDelete('plan', p.id); }} title="Удалить">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {expenseDetailsSubScreen === 'variable' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {selectedPeriodExpenses.filter(e => e.type !== 'fixed_payment' && e.is_paid !== false).map(e => (
                      <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--surface-border)', gap: '10px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                          <span style={{ fontWeight: '600' }}>{e.category || 'Разовый расход'}</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            {new Date(e.date).toLocaleDateString('ru-RU')} {e.comment ? `• ${e.comment}` : ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '700', color: 'var(--danger)' }}>-{formatMoney(Number(e.amount))}</span>
                          <button className="btn-icon-edit" style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }} onClick={() => { setShowExpensesDetails(false); openModal(e.fixed_plan_id ? 'expense_fixed' : 'expense_var', e.fixed_plan_id, e); }} title="Редактировать">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button className="btn-icon-delete" style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }} onClick={() => { setShowExpensesDetails(false); handleDelete('expense', e.id); }} title="Удалить">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                    {useEstimatedRevenue && forecastDaysCount > 0 && averageDailyVarExpensesPast30 > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'rgba(239, 68, 68, 0.05)', borderRadius: '10px', border: '1px dashed var(--danger)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={{ fontWeight: '600' }}>Переменные расходы (Прогноз)</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            {forecastDaysCount} дн. по {formatMoney(averageDailyVarExpensesPast30)}/день
                          </span>
                        </div>
                        <span style={{ fontWeight: '700', color: 'var(--danger)' }}>-{formatMoney(forecastDaysCount * averageDailyVarExpensesPast30)}</span>
                      </div>
                    )}
                    {selectedPeriodExpenses.filter(e => e.type !== 'fixed_payment' && e.is_paid !== false).length === 0 && (!useEstimatedRevenue || forecastDaysCount <= 0 || averageDailyVarExpensesPast30 <= 0) && (
                      <p className="text-secondary" style={{ textAlign: 'center', padding: '20px' }}>Нет переменных расходов за период</p>
                    )}
                  </div>
                )}

                {expenseDetailsSubScreen === 'taxes' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '4px' }}>
                    <div style={{ background: 'var(--surface-solid)', padding: '14px', borderRadius: '12px', border: '1px solid var(--surface-border)', fontSize: '13px', lineHeight: 1.5, color: 'var(--text-secondary)' }}>
                      Налог рассчитывается автоматически по ставке **6% (УСН Доходы)** от общей выручки за выбранный период.
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 4px', borderBottom: '1px solid var(--surface-border)' }}>
                      <span>Суммарная выручка</span>
                      <strong style={{ color: 'var(--success)' }}>{formatMoney(totalIncome)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 4px', borderBottom: '1px solid var(--surface-border)' }}>
                      <span>Налоговая ставка</span>
                      <strong>6%</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 4px', fontSize: '16px' }}>
                      <span style={{ fontWeight: '700' }}>Начислено налога</span>
                      <strong style={{ color: '#3B82F6', fontSize: '18px' }}>{formatMoney(taxAmount)}</strong>
                    </div>
                  </div>
                )}

                {expenseDetailsSubScreen === 'partners' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {selectedPeriodPartnerTurnovers.map(t => {
                      const partner = partners.find(p => p.id === t.partner_id)
                      const partnerName = partner?.name || 'Не указан'
                      const pct = partner?.commission_percent || 0
                      return (
                        <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--surface-border)', gap: '12px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: '700', fontSize: '15px' }}>{partnerName}</span>
                              <span style={{ fontSize: '11px', color: 'var(--text-secondary)', background: 'var(--surface-border)', padding: '2px 8px', borderRadius: '6px', fontWeight: 500 }}>{pct}%</span>
                            </div>
                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                              Оборот: <strong style={{ color: 'var(--text-primary)' }}>{formatMoney(Number(t.turnover_amount))}</strong>
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                              {new Date(t.entry_date).toLocaleDateString('ru-RU')} {t.comment ? `• ${t.comment}` : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>Комиссия</span>
                              <span style={{ fontWeight: '800', color: '#8B5CF6', fontSize: '16px' }}>{formatMoney(Number(t.commission_amount))}</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-secondary)' }} onClick={() => { setShowExpensesDetails(false); setEditPartner({ ...partner, editEntry: t }); setPartnerModal('edit_turnover') }} title="Редактировать">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                              </button>
                              <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-secondary)' }} onClick={async () => {
                                if (!confirm('Удалить запись комиссии?')) return
                                try { 
                                  await api.deletePartnerTurnover(t.id); 
                                  toast.success('Успешно удалено'); 
                                  setShowExpensesDetails(false);
                                  loadData(true) 
                                } catch (e) { toast.error(e.message) }
                              }} title="Удалить">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                    {useEstimatedRevenue && partners.map(partner => {
                      const forecastAmt = partnerForecastDetails[partner.id]?.commission || 0
                      if (forecastAmt <= 0) return null
                      return (
                        <div key={`forecast-${partner.id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'rgba(139, 92, 246, 0.05)', borderRadius: '12px', border: '1px dashed #8B5CF6', gap: '12px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: '700', fontSize: '15px' }}>{partner.name} (Прогноз)</span>
                              <span style={{ fontSize: '11px', color: 'var(--text-secondary)', background: 'var(--surface-border)', padding: '2px 8px', borderRadius: '6px', fontWeight: 500 }}>{partner.commission_percent}%</span>
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                              Прогноз на {forecastDaysCount} дней по ставке партнёра
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>Комиссия</span>
                            <span style={{ fontWeight: '800', color: '#8B5CF6', fontSize: '16px', display: 'block' }}>{formatMoney(forecastAmt)}</span>
                          </div>
                        </div>
                      )
                    })}
                    {selectedPeriodPartnerTurnovers.length === 0 && (!useEstimatedRevenue || forecastDaysCount <= 0 || totalPartnerCommission <= 0) && (
                      <p className="text-secondary" style={{ textAlign: 'center', padding: '20px' }}>Нет начислений партнёрам за период</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>

      {showExportModal && (
        <ExportAnalyticsModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          activePvz={activePvz}
          organizationName={activePvz?.organization_name || activePvz?.name || 'ПВЗ Расчёт'}
          currentFilterPeriod={{
            startDate: filterStartDate,
            endDate: filterEndDate
          }}
          currentLoadedMetrics={currentLoadedMetrics}
          useEstimatedRevenue={useEstimatedRevenue}
          forecastAutoCalc={forecastAutoCalc}
          forecastManualRate={forecastManualRate}
        />
      )}

    </div>
  )
}
